/**
 * Form Runtime Components — Vue 3 component definitions
 *
 * Generates a self-contained JavaScript string that registers all
 * prm-* components in the Gate iframe runtime.
 *
 * Components: prm-form, prm-step, prm-section, prm-row, prm-submit,
 * and all prm-*-field types.
 */

/**
 * Serialize all form runtime Vue components into a JavaScript string
 * for embedding in the Gate iframe runtime.
 */
export const serializeFormComponentsForRuntime = (): string => {
    return `
(function() {
    var Vue = window.Vue;
    window.__prmComponents = window.__prmComponents || {};

    function getGate() {
        return window.Gate || null;
    }

    // ── Resolve label via i18n ──
    function resolveLabel(raw) {
        var gate = getGate();
        if (!raw) return "";
        if (gate && gate.i18n && gate.i18n.has(raw)) {
            return gate.i18n.t(raw, {}, raw);
        }
        return raw;
    }

    function runtimeText(key, fallback) {
        var gate = getGate();
        return gate && gate.i18n && gate.i18n.has && gate.i18n.has(key)
            ? gate.i18n.t(key, {}, fallback) : fallback;
    }

    function createPendingFormStore() {
        return Vue.reactive({
            __ready: false,
            __state: {
                values: {},
                fieldErrors: {},
                fieldInfo: {},
                overrides: {},
                submitting: false,
                submitted: false,
                loading: false,
                effectiveMode: "create"
            },
            __fieldDefs: {},
            __fieldOrder: [],
            __steps: [],
            __isMultiStep: false,
            __ruleWarnings: [],
            __parsedRules: {},
            getValue: function() { return null; },
            getValues: function() { return {}; },
            setValue: function() {},
            setValues: function() {},
            validate: function() { return true; },
            validateStep: function() { return true; },
            getErrors: function() { return {}; },
            getCurrentStep: function() { return 0; },
            getStepCount: function() { return 1; },
            goToStep: function() {},
            nextStep: function() { return false; },
            previousStep: function() { return false; },
            getMapping: function() { return {}; },
            getTargetTable: function() { return ""; },
            onFieldChange: function() {},
            onStepChange: function() {},
            __getFieldState: function(name) {
                return { visible: true, required: false, readonly: false, disabled: false, label: name };
            },
            __getMappedValues: function() { return {}; },
            submit: null
        });
    }

    function normalizeVNodeChildren(children) {
        if (!children) return [];
        if (Array.isArray(children)) return children;
        if (typeof children === "object" && typeof children.default === "function") {
            try {
                return children.default() || [];
            } catch (_e) {
                return [];
            }
        }
        return [];
    }

    function getPrmTagFromVNode(vnode) {
        if (!vnode || !vnode.type) return "";
        if (typeof vnode.type === "string" && vnode.type.indexOf("prm-") === 0) {
            return vnode.type;
        }

        if (typeof vnode.type === "object") {
            var componentName = vnode.type.__prmTagName || vnode.type.name || vnode.type.__name || vnode.type.displayName || "";
            if (typeof componentName === "string" && componentName.indexOf("prm-") === 0) {
                return componentName;
            }
        }

        var components = window.__prmComponents || {};
        var names = Object.keys(components);
        for (var i = 0; i < names.length; i++) {
            if (components[names[i]] === vnode.type) {
                return names[i];
            }
        }
        return "";
    }

    function copyDefinitionAttrs(target, props) {
        if (!props || typeof props !== "object") return;
        Object.keys(props).forEach(function(key) {
            var value = props[key];
            if (value === undefined || value === null || typeof value === "function") return;
            target.setAttribute(key, String(value));
        });
    }

    function appendDefinitionNodes(parent, vnodes) {
        (vnodes || []).forEach(function(vnode) {
            var tag = getPrmTagFromVNode(vnode);
            var props = vnode && vnode.props ? vnode.props : {};

            if (tag === "prm-step") {
                var stepEl = document.createElement("prm-step");
                copyDefinitionAttrs(stepEl, props);
                parent.appendChild(stepEl);
                appendDefinitionNodes(stepEl, normalizeVNodeChildren(vnode.children));
                return;
            }

            if (/^prm-.+-field$/.test(tag)) {
                var fieldEl = document.createElement(tag);
                copyDefinitionAttrs(fieldEl, props);
                parent.appendChild(fieldEl);
                appendDefinitionNodes(fieldEl, normalizeVNodeChildren(vnode.children));
                return;
            }

            if (tag === "prm-choice-field-item") {
                var choiceItemEl = document.createElement("prm-choice-field-item");
                copyDefinitionAttrs(choiceItemEl, props);
                parent.appendChild(choiceItemEl);
                return;
            }

            appendDefinitionNodes(parent, normalizeVNodeChildren(vnode && vnode.children));
        });
    }

    function buildFormDefinitionElement(component) {
        var formEl = document.createElement("prm-form");
        copyDefinitionAttrs(formEl, component.$attrs || {});
        copyDefinitionAttrs(formEl, component.$props || {});
        if (component.$slots && typeof component.$slots.default === "function") {
            appendDefinitionNodes(formEl, component.$slots.default() || []);
        }
        return formEl;
    }

    function normalizeFormTemplate(template) {
        return String(template || "").replace(new RegExp("<(prm-[a-z0-9-]+)([^>]*)/>", "gi"), "<$1$2></$1>");
    }

    function toCamelCase(value) {
        return String(value || "").replace(/-([a-z])/g, function(_match, chr) { return chr.toUpperCase(); });
    }

    function addPrmAttributeAliases(attrs, name, value) {
        if (!name || name.indexOf("prm:") !== 0) return;
        var rawName = name.slice(4);
        var kebabName = "prm-" + rawName;
        attrs[kebabName] = value;
        attrs[toCamelCase(kebabName)] = value;
        attrs[rawName] = value;
        attrs[toCamelCase(rawName)] = value;
    }

    function attrsToObject(element) {
        var attrs = {};
        if (!element || !element.attributes) return attrs;
        Array.prototype.forEach.call(element.attributes, function(attr) {
            attrs[attr.name] = attr.value;
            addPrmAttributeAliases(attrs, attr.name, attr.value);
        });
        return attrs;
    }

    function createRuntimeVNode(node, context) {
        if (!node) return null;
        context = context || {};

        if (node.nodeType === 3) {
            return node.textContent && node.textContent.trim() ? node.textContent : null;
        }

        if (node.nodeType !== 1) return null;

        var tag = node.tagName ? node.tagName.toLowerCase() : "";
        if (!tag) return null;

        var childContext = { parentTag: tag, stepIndex: 0 };
        var children = Array.prototype.map.call(node.childNodes || [], function(childNode) {
            if (tag === "prm-form" && childNode.nodeType === 1 && childNode.tagName && childNode.tagName.toLowerCase() === "prm-step") {
                var stepVNode = createRuntimeVNode(childNode, { parentTag: tag, stepIndex: childContext.stepIndex });
                childContext.stepIndex += 1;
                return stepVNode;
            }
            return createRuntimeVNode(childNode, childContext);
        }).filter(function(child) {
            return child !== null && child !== undefined && child !== "";
        });
        var props = attrsToObject(node);
        if (tag === "prm-step" && context.parentTag === "prm-form") {
            props.stepIndex = context.stepIndex;
            props["step-index"] = context.stepIndex;
            props["data-prm-step-index"] = context.stepIndex;
        }
        var components = window.__prmComponents || {};
        var type = components[tag] || tag;
        var renderedChildren = children.length ? children : undefined;

        if (typeof type === "string") {
            return Vue.h(type, props, renderedChildren);
        }

        return Vue.h(type, props, renderedChildren ? { default: function() { return renderedChildren; } } : undefined);
    }

    function buildRuntimeRender(template) {
        var normalizedTemplate = normalizeFormTemplate(template);
        return function() {
            var parser = new DOMParser();
            var doc = parser.parseFromString("<!DOCTYPE html><html><body>" + normalizedTemplate + "</body></html>", "text/html");
            var children = Array.prototype.map.call(doc.body.childNodes || [], function(childNode) {
                return createRuntimeVNode(childNode, {});
            }).filter(function(child) {
                return child !== null && child !== undefined && child !== "";
            });

            if (children.length === 1) return children[0];
            return Vue.h("div", {}, children);
        };
    }

    window.__prmCreateFormRuntimeComponent = function(input) {
        var options = input && typeof input === "object" ? input : {};
        var template = normalizeFormTemplate(options.template || "");
        var script = options.script || "";
        var catalog = options.catalog || null;
        var runtimeGate = options.gate || getGate();
        var onError = typeof options.onError === "function" ? options.onError : null;
        var component = {};

        if (!template.trim()) {
            return null;
        }

        if (script && String(script).trim()) {
            try {
                component = (new Function("Vue", "Gate", "form", "return " + script))(Vue, runtimeGate, catalog) || {};
            } catch (error) {
                console.error("[prm-form] Failed to compile form script_code", error);
                if (onError) onError(error);
                component = {};
            }
        }

        if (!component.template && !component.render) component.render = buildRuntimeRender(template);

        var originalData = component.data;
        var originalProvide = component.provide;
        var prmComponents = window.__prmComponents || {};
        var originalComponents = component.components && typeof component.components === "object" ? component.components : {};

        return Object.assign({}, component, {
            name: component.name || "prm-runtime-form",
            components: Object.assign({}, prmComponents, originalComponents),
            data: function() {
                var base = typeof originalData === "function" ? originalData.call(this) : {};
                return Object.assign({}, base && typeof base === "object" ? base : {}, {
                    Gate: runtimeGate,
                    formCatalog: catalog,
                    // A2 (design_form_update_mode_and_fluss_dispatch.md): mirrors formCatalog --
                    // whoever calls this (a host page) can pass { table, recordId } the same way
                    // it already passes a catalog.
                    formRecordContext: options.recordContext || null,
                    formInviteContext: options.inviteContext || null
                });
            },
            provide: function() {
                var provided = typeof originalProvide === "function"
                    ? originalProvide.call(this)
                    : (originalProvide && typeof originalProvide === "object" ? originalProvide : {});
                return Object.assign({}, provided, {
                    formCatalog: catalog,
                    formGate: runtimeGate,
                    formRecordContext: options.recordContext || null,
                    formInviteContext: options.inviteContext || null
                });
            }
        });
    };

    // ── Base field mixin ──
    var fieldMixin = {
        inject: ["formStore"],
        props: {
            name: { type: String, required: true },
            label: { type: String, default: "" },
            required: { type: [String, Boolean], default: false },
            placeholder: { type: String, default: "" },
            minlength: { type: [String, Number], default: "" },
            maxlength: { type: [String, Number], default: "" },
            pattern: { type: String, default: "" },
            accept: { type: String, default: "" },
            variant: { type: String, default: "" },
            multiple: { type: [String, Boolean], default: false },
            min: { type: [String, Number], default: "" },
            max: { type: [String, Number], default: "" },
            currencyCode: { type: String, default: "" },
            currencyField: { type: String, default: "" }
        },
        methods: {
            clearLabel: function() {
                var gate = getGate();
                var key = "forms.reference.clear";
                var fallback = "Clear selection";
                return gate && gate.i18n && gate.i18n.has && gate.i18n.has(key)
                    ? gate.i18n.t(key, {}, fallback)
                    : fallback;
            }
        },
        computed: {
            nativeMaxlength: function() {
                var min = Number(this.minlength);
                var max = Number(this.maxlength);
                return this.minlength !== "" && this.maxlength !== "" && Number.isFinite(min) && Number.isFinite(max) && min > max
                    ? undefined : (this.maxlength || undefined);
            },
            fieldState: function() {
                return this.formStore.__getFieldState(this.name);
            },
            fieldValue: {
                get: function() { return this.formStore.getValue(this.name); },
                set: function(val) { this.formStore.setValue(this.name, val); }
            },
            resolvedLabel: function() {
                return resolveLabel(this.fieldState.label || this.label || this.name);
            },
            resolvedPlaceholder: function() {
                return resolveLabel(this.placeholder);
            },
            fieldErrors: function() {
                return this.formStore.__state.fieldErrors[this.name] || [];
            },
            fieldInfo: function() {
                return this.formStore.__state.fieldInfo[this.name] || [];
            },
            isVisible: function() { return this.fieldState.visible; },
            isRequired: function() { return this.fieldState.required; },
            isReadonly: function() { return this.fieldState.readonly; },
            isDisabled: function() { return this.fieldState.disabled; },
            fieldClasses: function() {
                return {
                    "prm-field": true,
                    "prm-field--required": this.isRequired,
                    "prm-field--error": this.fieldErrors.length > 0,
                    "prm-field--readonly": this.isReadonly,
                    "prm-field--disabled": this.isDisabled
                };
            }
        }
    };

    // ── Wrapper template for fields ──
    var fieldWrapperTemplate = function(inputSlot) {
        inputSlot = inputSlot.replace(/:id="name"/g, ':id="name" :aria-invalid="fieldErrors.length ? &quot;true&quot; : undefined" :aria-describedby="fieldErrors.length ? name + &quot;-errors&quot; : undefined"');
        return '<div v-if="isVisible" :class="fieldClasses" class="prm-field-wrapper">' +
            '<label class="prm-field__label" :for="name">{{ resolvedLabel }}' +
                '<span v-if="isRequired" class="prm-field__required"> *</span>' +
            '</label>' +
            inputSlot +
            '<div v-if="fieldErrors.length" class="prm-field__errors" :id="name + &quot;-errors&quot;">' +
                '<span v-for="(err, idx) in fieldErrors" :key="idx" class="prm-field__error">{{ err }}</span>' +
            '</div>' +
            '<div v-if="fieldInfo.length" class="prm-field__info-list">' +
                '<span v-for="(info, idx) in fieldInfo" :key="idx" class="prm-field__info">{{ info }}</span>' +
            '</div>' +
        '</div>';
    };

    // ══════════════════════════════════════════════════
    // prm-text-field
    // ══════════════════════════════════════════════════
    window.__prmComponents["prm-text-field"] = {
        mixins: [fieldMixin],
        template: fieldWrapperTemplate(
            '<input type="text" class="prm-field__input" :id="name" ' +
            'v-model="fieldValue" :placeholder="resolvedPlaceholder" ' +
            ':readonly="isReadonly" :disabled="isDisabled" :minlength="minlength || undefined" :maxlength="nativeMaxlength" @blur="formStore.validateField && formStore.validateField(name)" />'
        )
    };

    // ══════════════════════════════════════════════════
    // prm-textarea-field
    // ══════════════════════════════════════════════════
    window.__prmComponents["prm-textarea-field"] = {
        mixins: [fieldMixin],
        template: fieldWrapperTemplate(
            '<textarea class="prm-field__input prm-field__textarea" :id="name" ' +
            'v-model="fieldValue" :placeholder="resolvedPlaceholder" ' +
            ':readonly="isReadonly" :disabled="isDisabled" :minlength="minlength || undefined" :maxlength="nativeMaxlength" @blur="formStore.validateField && formStore.validateField(name)" rows="4"></textarea>'
        )
    };

    // ══════════════════════════════════════════════════
    // prm-email-field
    // ══════════════════════════════════════════════════
    window.__prmComponents["prm-email-field"] = {
        mixins: [fieldMixin],
        template: fieldWrapperTemplate(
            '<input type="email" class="prm-field__input" :id="name" ' +
            'v-model="fieldValue" :placeholder="resolvedPlaceholder" ' +
            ':readonly="isReadonly" :disabled="isDisabled" :minlength="minlength || undefined" :maxlength="nativeMaxlength" @blur="formStore.validateField && formStore.validateField(name)" />'
        )
    };

    // ══════════════════════════════════════════════════
    // prm-url-field
    // ══════════════════════════════════════════════════
    window.__prmComponents["prm-url-field"] = {
        mixins: [fieldMixin],
        template: fieldWrapperTemplate(
            '<input type="url" class="prm-field__input" :id="name" ' +
            'v-model="fieldValue" :placeholder="resolvedPlaceholder" ' +
            ':readonly="isReadonly" :disabled="isDisabled" :minlength="minlength || undefined" :maxlength="nativeMaxlength" @blur="formStore.validateField && formStore.validateField(name)" />'
        )
    };

    // ══════════════════════════════════════════════════
    // prm-password-field
    // ══════════════════════════════════════════════════
    window.__prmComponents["prm-password-field"] = {
        mixins: [fieldMixin],
        data: function() { return { showPassword: false }; },
        computed: {
            passwordShowLabel: function() { return runtimeText("forms.password.show", "Show"); },
            passwordHideLabel: function() { return runtimeText("forms.password.hide", "Hide"); },
            passwordStrength: function() {
                var value = String(this.fieldValue || "");
                var score = 0;
                if (value.length >= 8) score++;
                if (/[A-Z]/.test(value)) score++;
                if (/[0-9]/.test(value)) score++;
                if (/[^A-Za-z0-9]/.test(value)) score++;
                return score;
            }
        },
        template: fieldWrapperTemplate(
            '<div class="prm-field__password">' +
                '<div class="relative">' +
                    '<input :type="showPassword ? &quot;text&quot; : &quot;password&quot;" class="prm-field__input pr-12" :id="name" ' +
                    'v-model="fieldValue" :placeholder="resolvedPlaceholder" @blur="formStore.validateField && formStore.validateField(name)" ' +
                    ':readonly="isReadonly" :disabled="isDisabled" :minlength="minlength || undefined" :maxlength="nativeMaxlength" :pattern="pattern || undefined" />' +
                    '<button type="button" class="absolute right-2 top-1/2 -translate-y-1/2 rounded px-2 text-sm text-[var(--prm-text-secondary,#6b7280)] hover:text-[var(--prm-text,#111827)]" @click="showPassword = !showPassword" :disabled="isDisabled">{{ showPassword ? passwordHideLabel : passwordShowLabel }}</button>' +
                '</div>' +
                '<div class="mt-2 grid grid-cols-4 gap-1" aria-hidden="true">' +
                    '<span v-for="idx in 4" :key="idx" class="h-1 rounded-full transition-colors" :class="idx <= passwordStrength ? &quot;bg-prm-primary&quot; : &quot;bg-slate-200&quot;"></span>' +
                '</div>' +
            '</div>'
        )
    };

    // ══════════════════════════════════════════════════
    // prm-number-field
    // ══════════════════════════════════════════════════
    var NUMERIC_EDIT_KEYS = {
        Backspace: true,
        Delete: true,
        Tab: true,
        Enter: true,
        Escape: true,
        ArrowLeft: true,
        ArrowRight: true,
        ArrowUp: true,
        ArrowDown: true,
        Home: true,
        End: true
    };

    function hasTextSelection(input) {
        return input
            && input.selectionStart !== null
            && input.selectionEnd !== null
            && input.selectionStart !== input.selectionEnd;
    }

    function replaceInputSelection(input, replacement) {
        var value = input && typeof input.value === "string" ? input.value : "";
        var start = input && input.selectionStart !== null ? input.selectionStart : value.length;
        var end = input && input.selectionEnd !== null ? input.selectionEnd : value.length;
        return value.slice(0, start) + replacement + value.slice(end);
    }

    function sanitizeIntegerText(value) {
        var raw = String(value || "");
        var isNegative = raw.trim().charAt(0) === "-";
        var digits = raw.replace(/[^0-9]/g, "");
        return isNegative ? "-" + digits : digits;
    }

    function integerValueFromText(value) {
        if (!value || value === "-") return null;
        return Number(value);
    }

    window.__prmComponents["prm-number-field"] = {
        mixins: [fieldMixin],
        data: function() {
            return { textValue: this.fieldValue === null || this.fieldValue === undefined ? "" : String(Math.trunc(Number(this.fieldValue))) };
        },
        template: fieldWrapperTemplate(
            '<input type="text" inputmode="numeric" class="prm-field__input" :id="name" ' +
            ':value="textValue" @keydown="onKeyDown" @input="onInput" @paste="onPaste" @blur="onBlur" :placeholder="resolvedPlaceholder" ' +
            ':readonly="isReadonly" :disabled="isDisabled" />'
        ),
        watch: {
            fieldValue: function(value) {
                var nextText = value === null || value === undefined || value === "" ? "" : String(Math.trunc(Number(value)));
                if (nextText !== this.textValue) this.textValue = nextText;
            }
        },
        methods: {
            commitText: function(nextText) {
                var sanitized = sanitizeIntegerText(nextText);
                this.textValue = sanitized;
                this.fieldValue = integerValueFromText(sanitized);
            },
            onKeyDown: function(event) {
                if (this.isDisabled || this.isReadonly) return;
                if (event.ctrlKey || event.metaKey || event.altKey) return;
                if (NUMERIC_EDIT_KEYS[event.key]) return;
                if (event.key >= "0" && event.key <= "9") return;

                if (event.key === "-") {
                    var input = event.target instanceof HTMLInputElement ? event.target : null;
                    var selectionStartsAtZero = input && input.selectionStart === 0;
                    var canReplaceSelection = input && hasTextSelection(input) && input.selectionStart === 0;
                    var hasMinusOutsideSelection = input
                        ? replaceInputSelection(input, "").indexOf("-") >= 0
                        : this.textValue.indexOf("-") >= 0;

                    if ((selectionStartsAtZero || canReplaceSelection) && !hasMinusOutsideSelection) return;
                }

                event.preventDefault();
            },
            onInput: function(event) {
                var raw = event && event.target ? event.target.value : "";
                var sanitized = sanitizeIntegerText(raw);
                if (event && event.target && event.target.value !== sanitized) {
                    event.target.value = sanitized;
                }
                this.commitText(sanitized);
            },
            onPaste: function(event) {
                if (this.isDisabled || this.isReadonly) return;
                event.preventDefault();
                var clipboardData = event.clipboardData || window.clipboardData;
                var pastedValue = clipboardData ? clipboardData.getData("text") : "";
                var input = event.target instanceof HTMLInputElement ? event.target : null;
                this.commitText(sanitizeIntegerText(replaceInputSelection(input, pastedValue)));
            },
            onBlur: function() {
                this.commitText(this.textValue);
                if (this.formStore.validateField) this.formStore.validateField(this.name);
            }
        }
    };

    // ══════════════════════════════════════════════════
    // prm-decimal-field
    // ══════════════════════════════════════════════════
    function getRuntimeDecimalSeparator() {
        var preferences = getRuntimeCurrencyPreferences();
        var separators = getRuntimeNumberSeparators(preferences);
        return separators.decimal;
    }

    function normalizeDecimalText(value, decimalSeparator, allowAnySeparator) {
        var raw = String(value || "").trim();
        var isNegative = raw.charAt(0) === "-";
        var allowedSeparator = decimalSeparator || ".";
        var cleaned = "";
        var lastSeparatorIndex = -1;
        var i;

        for (i = 0; i < raw.length; i++) {
            var char = raw.charAt(i);
            if (char >= "0" && char <= "9") {
                cleaned += char;
            } else if (allowAnySeparator && (char === "." || char === ",")) {
                cleaned += char;
                lastSeparatorIndex = cleaned.length - 1;
            } else if (!allowAnySeparator && char === allowedSeparator && lastSeparatorIndex < 0) {
                cleaned += char;
                lastSeparatorIndex = cleaned.length - 1;
            }
        }

        if (allowAnySeparator && lastSeparatorIndex >= 0) {
            var before = cleaned.slice(0, lastSeparatorIndex).replace(/[.,]/g, "");
            var after = cleaned.slice(lastSeparatorIndex + 1).replace(/[.,]/g, "");
            cleaned = before + allowedSeparator + after;
        } else if (!allowAnySeparator && lastSeparatorIndex >= 0) {
            cleaned = cleaned.slice(0, lastSeparatorIndex + 1) + cleaned.slice(lastSeparatorIndex + 1).replace(new RegExp("\\\\" + allowedSeparator, "g"), "");
        }

        return isNegative ? "-" + cleaned : cleaned;
    }

    function decimalValueFromText(value, decimalSeparator) {
        if (!value || value === "-" || value === decimalSeparator || value === "-" + decimalSeparator) return null;
        var normalized = String(value).replace(decimalSeparator, ".");
        var numberValue = Number(normalized);
        return Number.isFinite(numberValue) ? numberValue : null;
    }

    function decimalTextFromValue(value, decimalSeparator) {
        if (value === null || value === undefined || value === "") return "";
        return String(value).replace(".", decimalSeparator || ".");
    }

    window.__prmComponents["prm-decimal-field"] = {
        mixins: [fieldMixin],
        data: function() {
            return { textValue: decimalTextFromValue(this.fieldValue, getRuntimeDecimalSeparator()) };
        },
        template: fieldWrapperTemplate(
            '<input type="text" inputmode="decimal" class="prm-field__input" :id="name" ' +
            ':value="textValue" @keydown="onKeyDown" @input="onInput" @paste="onPaste" @blur="onBlur" :placeholder="resolvedPlaceholder" ' +
            ':readonly="isReadonly" :disabled="isDisabled" />'
        ),
        computed: {
            decimalSeparator: function() {
                return getRuntimeDecimalSeparator();
            }
        },
        watch: {
            fieldValue: function(value) {
                var nextText = decimalTextFromValue(value, this.decimalSeparator);
                if (nextText !== this.textValue) this.textValue = nextText;
            },
            decimalSeparator: function(nextSeparator) {
                this.textValue = decimalTextFromValue(this.fieldValue, nextSeparator);
            }
        },
        methods: {
            commitText: function(nextText, allowAnySeparator) {
                var sanitized = normalizeDecimalText(nextText, this.decimalSeparator, !!allowAnySeparator);
                this.textValue = sanitized;
                this.fieldValue = decimalValueFromText(sanitized, this.decimalSeparator);
            },
            onKeyDown: function(event) {
                if (this.isDisabled || this.isReadonly) return;
                if (event.ctrlKey || event.metaKey || event.altKey) return;
                if (NUMERIC_EDIT_KEYS[event.key]) return;
                if (event.key >= "0" && event.key <= "9") return;

                var input = event.target instanceof HTMLInputElement ? event.target : null;
                var withoutSelection = input ? replaceInputSelection(input, "") : this.textValue;

                if (event.key === "-") {
                    var selectionStartsAtZero = input && input.selectionStart === 0;
                    var canReplaceSelection = input && hasTextSelection(input) && input.selectionStart === 0;
                    if ((selectionStartsAtZero || canReplaceSelection) && withoutSelection.indexOf("-") < 0) return;
                }

                if (event.key === this.decimalSeparator) {
                    if (withoutSelection.indexOf(this.decimalSeparator) < 0) return;
                }

                event.preventDefault();
            },
            onInput: function(event) {
                var raw = event && event.target ? event.target.value : "";
                var sanitized = normalizeDecimalText(raw, this.decimalSeparator, false);
                if (event && event.target && event.target.value !== sanitized) {
                    event.target.value = sanitized;
                }
                this.commitText(sanitized, false);
            },
            onPaste: function(event) {
                if (this.isDisabled || this.isReadonly) return;
                event.preventDefault();
                var clipboardData = event.clipboardData || window.clipboardData;
                var pastedValue = clipboardData ? clipboardData.getData("text") : "";
                var input = event.target instanceof HTMLInputElement ? event.target : null;
                this.commitText(replaceInputSelection(input, pastedValue), true);
            },
            onBlur: function() {
                this.textValue = decimalTextFromValue(this.fieldValue, this.decimalSeparator);
                if (this.formStore.validateField) this.formStore.validateField(this.name);
            }
        }
    };

    // ══════════════════════════════════════════════════
    // prm-currency-field
    // ══════════════════════════════════════════════════
    var DEFAULT_RUNTIME_NUMBER_FORMAT = "COMMA_THOUSANDS_DOT_DECIMAL";

    function getRuntimeCurrencyPreferences() {
        var gate = getGate();
        var currency = gate && gate.currency && typeof gate.currency === "object" ? gate.currency : {};
        return {
            currencies: Array.isArray(currency.currencies) ? currency.currencies : [],
            defaultCurrencyCode: currency.defaultCurrencyCode || null,
            defaultNumberFormat: currency.defaultNumberFormat || null,
            userNumberFormat: currency.userNumberFormat || null
        };
    }

    function findRuntimeCurrencyRule(codeOrId, preferences) {
        var value = codeOrId === undefined || codeOrId === null ? "" : String(codeOrId).trim();
        if (!value) return null;

        var upperValue = value.toUpperCase();
        return (preferences.currencies || []).find(function(currency) {
            var id = currency.id === undefined || currency.id === null ? "" : String(currency.id);
            var code = currency.code === undefined || currency.code === null ? "" : String(currency.code).toUpperCase();
            return id === value || code === upperValue;
        }) || null;
    }

    function getRuntimeCurrencyRule(codeOrId, preferences) {
        var rule = findRuntimeCurrencyRule(codeOrId, preferences);
        if (rule) return rule;

        return findRuntimeCurrencyRule(preferences.defaultCurrencyCode, preferences);
    }

    function normalizeRuntimeDecimalPlaces(value) {
        var numberValue = Number(value);
        if (!Number.isFinite(numberValue) || numberValue < 0) return 2;
        return Math.floor(numberValue);
    }

    function getRuntimeNumberSeparators(preferences) {
        var numberFormat = preferences.userNumberFormat || preferences.defaultNumberFormat || DEFAULT_RUNTIME_NUMBER_FORMAT;

        if (numberFormat === "DOT_THOUSANDS_COMMA_DECIMAL") {
            return { decimal: ",", thousand: "." };
        }

        return { decimal: ".", thousand: "," };
    }

    function stripLeadingCurrencyZeros(value) {
        return String(value || "").replace(/^0+(?=[0-9])/, "");
    }

    function digitsFromCanonicalCurrency(value, decimalPlaces) {
        if (value === null || value === undefined || value === "") return "";

        var normalized = String(value).replace(",", ".").trim();
        var isNegative = normalized.charAt(0) === "-";
        var unsigned = isNegative ? normalized.slice(1) : normalized;
        var parts = unsigned.split(".");
        var integerPart = (parts[0] || "").replace(/[^0-9]/g, "");
        var fractionPart = (parts[1] || "").replace(/[^0-9]/g, "");
        var unsignedDigits = decimalPlaces === 0
            ? integerPart
            : stripLeadingCurrencyZeros(integerPart + fractionPart.padEnd(decimalPlaces, "0").slice(0, decimalPlaces));

        return isNegative && unsignedDigits ? "-" + unsignedDigits : unsignedDigits;
    }

    function buildCanonicalCurrencyValue(digits, decimalPlaces) {
        if (!digits || digits === "-") return null;

        var isNegative = digits.charAt(0) === "-";
        var unsignedDigits = String(digits).replace("-", "");

        if (decimalPlaces === 0) {
            return isNegative ? "-" + unsignedDigits : unsignedDigits;
        }

        if (unsignedDigits.length <= decimalPlaces) {
            var canonical = "0." + unsignedDigits.padStart(decimalPlaces, "0");
            return isNegative ? "-" + canonical : canonical;
        }

        var integerPart = unsignedDigits.slice(0, -decimalPlaces);
        var fractionPart = unsignedDigits.slice(-decimalPlaces);
        var value = integerPart + "." + fractionPart;

        return isNegative ? "-" + value : value;
    }

    function groupCurrencyInteger(integerPart, separator) {
        var sign = integerPart.charAt(0) === "-" ? "-" : "";
        var digits = sign ? integerPart.slice(1) : integerPart;
        var output = "";

        while (digits.length > 3) {
            output = separator + digits.slice(-3) + output;
            digits = digits.slice(0, -3);
        }

        return sign + (digits || "0") + output;
    }

    function formatRuntimeCurrencyAmount(value, decimalPlaces, preferences) {
        if (value === null || value === undefined || value === "") return "";

        var normalized = String(value).replace(",", ".").trim();
        var isNegative = normalized.charAt(0) === "-";
        var unsigned = isNegative ? normalized.slice(1) : normalized;
        var parts = unsigned.split(".");
        var integerPart = (parts[0] || "0").replace(/[^0-9]/g, "") || "0";
        var fractionPart = (parts[1] || "").replace(/[^0-9]/g, "");
        var separators = getRuntimeNumberSeparators(preferences);
        var groupedInteger = groupCurrencyInteger((isNegative ? "-" : "") + integerPart, separators.thousand);

        if (decimalPlaces <= 0) {
            return groupedInteger;
        }

        return groupedInteger + separators.decimal + fractionPart.padEnd(decimalPlaces, "0").slice(0, decimalPlaces);
    }

    function hasFullCurrencySelection(input) {
        return input
            && input.selectionStart !== null
            && input.selectionEnd !== null
            && input.selectionStart === 0
            && input.selectionEnd === input.value.length;
    }

    window.__prmComponents["prm-currency-field"] = {
        mixins: [fieldMixin],
        data: function() {
            return { digits: "" };
        },
        template: fieldWrapperTemplate(
            '<div class="prm-field__currency-wrap">' +
                '<span class="prm-field__currency-symbol">{{ resolvedCurrencyLabel }}</span>' +
                '<input type="text" inputmode="decimal" class="prm-field__input prm-field__input--currency" :id="name" ' +
                ':value="displayValue" @keydown="onKeyDown" @paste="onPaste" @input="onInput" @blur="onBlur" :placeholder="resolvedPlaceholder" ' +
                ':readonly="isReadonly" :disabled="isDisabled" />' +
            '</div>'
        ),
        computed: {
            currencyPreferences: function() {
                return getRuntimeCurrencyPreferences();
            },
            resolvedCurrencyCode: function() {
                var dynamicCode = this.currencyField ? this.formStore.getValue(this.currencyField) : "";
                return dynamicCode || this.currencyCode || this.currencyPreferences.defaultCurrencyCode || "";
            },
            resolvedCurrencyRule: function() {
                return getRuntimeCurrencyRule(this.resolvedCurrencyCode, this.currencyPreferences);
            },
            resolvedCurrencyLabel: function() {
                return (this.resolvedCurrencyRule && this.resolvedCurrencyRule.symbol) || this.resolvedCurrencyCode || "";
            },
            decimalPlaces: function() {
                return normalizeRuntimeDecimalPlaces(this.resolvedCurrencyRule ? this.resolvedCurrencyRule.decimalPlaces : 2);
            },
            displayValue: function() {
                var canonical = buildCanonicalCurrencyValue(this.digits, this.decimalPlaces);
                return canonical === null && this.digits === "-"
                    ? "-"
                    : formatRuntimeCurrencyAmount(canonical, this.decimalPlaces, this.currencyPreferences);
            }
        },
        watch: {
            fieldValue: {
                immediate: true,
                handler: function(value) {
                    this.digits = digitsFromCanonicalCurrency(value, this.decimalPlaces);
                }
            },
            decimalPlaces: function() {
                this.digits = digitsFromCanonicalCurrency(this.fieldValue, this.decimalPlaces);
            }
        },
        methods: {
            commitDigits: function(nextDigits) {
                this.digits = nextDigits;
                this.fieldValue = buildCanonicalCurrencyValue(nextDigits, this.decimalPlaces);
            },
            appendDigit: function(digit) {
                var isNegative = this.digits.charAt(0) === "-";
                var unsignedDigits = this.digits.replace("-", "");
                var nextUnsignedDigits = stripLeadingCurrencyZeros(unsignedDigits + digit);
                this.commitDigits(isNegative ? "-" + nextUnsignedDigits : nextUnsignedDigits);
            },
            replaceSelectedValue: function(nextDigits) {
                this.commitDigits(nextDigits);
            },
            removeLastDigit: function() {
                var isNegative = this.digits.charAt(0) === "-";
                var unsignedDigits = this.digits.replace("-", "");
                var nextUnsignedDigits = unsignedDigits.slice(0, -1);
                this.commitDigits(isNegative && nextUnsignedDigits ? "-" + nextUnsignedDigits : nextUnsignedDigits);
            },
            toggleNegative: function() {
                if (this.digits.charAt(0) === "-") {
                    this.commitDigits(this.digits.slice(1));
                    return;
                }

                this.commitDigits(this.digits ? "-" + this.digits : "-");
            },
            onKeyDown: function(event) {
                if (this.isDisabled || this.isReadonly) return;

                var input = event.target instanceof HTMLInputElement ? event.target : null;

                if (event.key >= "0" && event.key <= "9") {
                    event.preventDefault();
                    if (hasFullCurrencySelection(input)) {
                        this.replaceSelectedValue(event.key);
                        return;
                    }

                    this.appendDigit(event.key);
                    return;
                }

                if (event.key === "." || event.key === ",") {
                    event.preventDefault();
                    return;
                }

                if (event.key === "-") {
                    event.preventDefault();
                    if (hasFullCurrencySelection(input)) {
                        this.replaceSelectedValue("-");
                        return;
                    }

                    this.toggleNegative();
                    return;
                }

                if (event.key === "Backspace" || event.key === "Delete") {
                    event.preventDefault();
                    if (hasFullCurrencySelection(input)) {
                        this.replaceSelectedValue("");
                        return;
                    }

                    this.removeLastDigit();
                    return;
                }

                if (event.key === "Escape") {
                    event.preventDefault();
                    this.replaceSelectedValue("");
                }
            },
            onPaste: function(event) {
                if (this.isDisabled || this.isReadonly) return;

                event.preventDefault();
                var clipboardData = event.clipboardData || window.clipboardData;
                var pastedValue = clipboardData ? clipboardData.getData("text") : "";
                var isNegative = String(pastedValue || "").trim().charAt(0) === "-";
                var unsignedDigits = stripLeadingCurrencyZeros(String(pastedValue || "").replace(/[^0-9]/g, ""));
                var nextDigits = isNegative && unsignedDigits ? "-" + unsignedDigits : unsignedDigits;

                this.replaceSelectedValue(nextDigits);
            },
            onInput: function(event) {
                if (event && event.target) {
                    event.target.value = this.displayValue;
                }
            },
            onBlur: function() {
                if (this.formStore.validateField) {
                    this.formStore.validateField(this.name);
                }
            }
        }
    };

    // ══════════════════════════════════════════════════
    // prm-boolean-field
    // ══════════════════════════════════════════════════
    window.__prmComponents["prm-boolean-field"] = {
        mixins: [fieldMixin],
        computed: {
            booleanVariant: function() {
                return this.variant || this.$attrs["prm:variant"] || this.$attrs["prm-variant"] || "switch";
            },
            useSwitch: function() { return this.booleanVariant !== "checkbox"; }
        },
        template: '<div v-if="isVisible" :class="fieldClasses" class="prm-field-wrapper prm-field--boolean">' +
            '<label v-if="!useSwitch" class="prm-field__checkbox-label" :for="name">' +
                '<input type="checkbox" class="prm-field__checkbox" :id="name" :aria-invalid="fieldErrors.length ? &quot;true&quot; : undefined" :aria-describedby="fieldErrors.length ? name + &quot;-errors&quot; : undefined" v-model="fieldValue" :disabled="isDisabled || isReadonly" @blur="formStore.validateField && formStore.validateField(name)" />' +
                '<span class="prm-field__checkbox-text">{{ resolvedLabel }}</span>' +
                '<span v-if="isRequired" class="prm-field__required"> *</span>' +
            '</label>' +
            '<template v-else>' +
                '<label class="prm-field__label" :for="name">{{ resolvedLabel }}<span v-if="isRequired" class="prm-field__required"> *</span></label>' +
                '<button type="button" class="inline-flex items-center gap-3 text-left" role="switch" :id="name" :aria-checked="!!fieldValue" :aria-invalid="fieldErrors.length ? &quot;true&quot; : undefined" :aria-describedby="fieldErrors.length ? name + &quot;-errors&quot; : undefined" :disabled="isDisabled || isReadonly" @click="fieldValue = !fieldValue; formStore.validateField && formStore.validateField(name)">' +
                    '<span class="relative inline-flex h-6 w-11 shrink-0 rounded-full transition-colors" :class="fieldValue ? &quot;bg-prm-primary&quot; : &quot;bg-slate-300&quot;">' +
                        '<span class="absolute left-0.5 top-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform" :class="fieldValue ? &quot;translate-x-5&quot; : &quot;translate-x-0&quot;"></span>' +
                    '</span>' +
                '</button>' +
            '</template>' +
            '<div v-if="fieldErrors.length" class="prm-field__errors" :id="name + &quot;-errors&quot;">' +
                '<span v-for="(err, idx) in fieldErrors" :key="idx" class="prm-field__error">{{ err }}</span>' +
            '</div>' +
        '</div>'
    };

    // ══════════════════════════════════════════════════
    // prm-date-field
    // ══════════════════════════════════════════════════

    function dateFieldTemplate(inputType) {
        return fieldWrapperTemplate(
            '<div class="relative">' +
                '<input type="' + inputType + '" class="prm-field__input pr-10" :id="name" ' +
                'v-model="fieldValue" :readonly="isReadonly" :disabled="isDisabled" :min="min || undefined" :max="max || undefined" @blur="formStore.validateField && formStore.validateField(name)" />' +
                '<button v-if="fieldValue && !isDisabled && !isReadonly" type="button" class="prm-field__clear absolute right-2 top-1/2 -translate-y-1/2 rounded px-2 text-[var(--prm-text-secondary,#6b7280)] hover:text-[var(--prm-text,#111827)]" :aria-label="clearLabel()" @click="fieldValue = null" @keydown.enter.prevent="fieldValue = null" @keydown.space.prevent="fieldValue = null" :disabled="isDisabled || isReadonly">&times;</button>' +
            '</div>'
        );
    }

    window.__prmComponents["prm-date-field"] = {
        mixins: [fieldMixin],
        template: dateFieldTemplate("date")
    };

    window.__prmComponents["prm-datetime-field"] = {
        mixins: [fieldMixin],
        template: dateFieldTemplate("datetime-local")
    };

    window.__prmComponents["prm-time-field"] = {
        mixins: [fieldMixin],
        template: dateFieldTemplate("time")
    };

    // ══════════════════════════════════════════════════
    // prm-notice-field: read-only content, visibility follows the field rule.
    // ══════════════════════════════════════════════════
    window.__prmComponents["prm-notice-field"] = {
        mixins: [fieldMixin],
        computed: {
            noticeSeverity: function() { return this.variant === "warning" ? "warning" : "info"; }
        },
        template: '<div v-if="isVisible" class="prm-notice" :class="noticeSeverity" role="status">{{ resolvedLabel }}</div>'
    };

    // ══════════════════════════════════════════════════
    // prm-choice-field
    // ══════════════════════════════════════════════════
    window.__prmComponents["prm-choice-field"] = {
        mixins: [fieldMixin],
        inject: ["formStore", "formCatalog"],
        props: {
            options: { type: String, default: "" }
        },
        data: function() {
            return {
                searchQuery: "",
                showDropdown: false,
                activeIndex: -1,
                dynamicOptions: [],
                loadingDynamic: false
            };
        },
        computed: {
            choiceSelectLabel: function() { return runtimeText("forms.choice.select", "Select…"); },
            parsedOptions: function() {
                var ovr = this.formStore.__state.overrides[this.name];
                if (ovr && ovr.options) return ovr.options;

                if (this.getSourceConfig().table) return this.dynamicOptions;

                var slotOptions = this.slotOptions();
                if (slotOptions.length) return slotOptions;

                var mappedColumn = this.$attrs["prm:map"] || this.$attrs["prm-map"] || "";
                var catalogChoices = this.formCatalog && this.formCatalog.choice_options;
                if (mappedColumn && catalogChoices && Array.isArray(catalogChoices[mappedColumn])) {
                    return catalogChoices[mappedColumn].map(function(option) {
                        return { value: String(option.value), label: resolveLabel(option.label) };
                    });
                }

                if (!this.options) return [];
                return this.options.split(",").map(function(item) {
                    var parts = item.trim().split(":");
                    if (parts.length === 1) return { value: parts[0].trim(), label: resolveLabel(parts[0].trim()) };
                    return { value: parts[0].trim(), label: resolveLabel(parts.slice(1).join(":").trim()) };
                });
            },
            selectedOption: function() {
                return this.parsedOptions.find(function(opt) { return opt.value === this.fieldValue; }.bind(this)) || null;
            },
            filteredOptions: function() {
                var query = String(this.searchQuery || "").trim().toLowerCase();
                if (!query) return this.parsedOptions;
                return this.parsedOptions.filter(function(opt) {
                    return String(opt.label || "").toLowerCase().indexOf(query) >= 0 || String(opt.value || "").toLowerCase().indexOf(query) >= 0;
                });
            },
            displayText: function() {
                return this.showDropdown ? this.searchQuery : (this.selectedOption ? this.selectedOption.label : "");
            },
            // Reactive key tracking every $form.<field> value referenced by
            // prm:reference-filter — changes whenever a dependency the filter
            // relies on changes, so dynamic options reload automatically
            // instead of staying frozen at whatever they were on mount.
            sourceFilterDependencyKey: function() {
                var filterAttr = this.getSourceConfig().filterAttr;
                if (!filterAttr) return "";
                var tokens = String(filterAttr).match(new RegExp("\\$form\\.[a-zA-Z0-9_]+", "g")) || [];
                var self = this;
                return tokens.map(function(token) {
                    return token + "=" + self.formStore.getValue(token.slice(6));
                }).join("|");
            }
        },
        mounted: function() {
            document.addEventListener("click", this.onDocumentClick);
            if (this.getSourceConfig().table) this.loadDynamicOptions();
        },
        beforeUnmount: function() {
            document.removeEventListener("click", this.onDocumentClick);
        },
        watch: {
            sourceFilterDependencyKey: function() {
                if (this.getSourceConfig().table) this.loadDynamicOptions();
            }
        },
        methods: {
            isInteractive: function() {
                return !this.isDisabled && !this.isReadonly;
            },
            // Dynamic (source-table-backed) options — same prm:source/prm:display/
            // prm:reference-filter attributes prm-reference-field already reads, plus
            // a choice-specific prm:value-field (defaults to "id" when unset). Loaded
            // once on mount as a bounded list (not search-as-you-type like the
            // reference field): a plain dropdown is expected to show its full option
            // set up front, and the existing client-side filteredOptions search above
            // already narrows it locally once loaded.
            getSourceConfig: function() {
                return {
                    table: this.$attrs["prm:source"] || this.$attrs["prm-source"] || "",
                    display: this.$attrs["prm:display"] || this.$attrs["prm-display"] || "",
                    valueField: this.$attrs["prm:value-field"] || this.$attrs["prm-value-field"] || "id",
                    filterAttr: this.$attrs["prm:reference-filter"] || this.$attrs["prm-reference-filter"] || ""
                };
            },
            resolveSourceFilterValue: function(value) {
                if (typeof value === "string" && value.indexOf("$form.") === 0) {
                    var resolved = this.formStore.getValue(value.slice(6));
                    if (resolved === undefined || resolved === null || resolved === "") this._sourceFilterIncomplete = true;
                    return resolved;
                }
                if (Array.isArray(value)) return value.map(this.resolveSourceFilterValue.bind(this));
                if (value && typeof value === "object") {
                    var output = {};
                    Object.keys(value).forEach(function(key) { output[key] = this.resolveSourceFilterValue(value[key]); }.bind(this));
                    return output;
                }
                return value;
            },
            // Sets this._sourceFilterIncomplete when the filter references a
            // $form.<field> value that isn't filled in yet — callers must check
            // it after calling this and skip querying rather than sending a
            // condition with a missing value (backend rejects it, and a filter
            // that can't be fully applied should show no options, not all of them).
            getSourceFilter: function() {
                var filterAttr = this.getSourceConfig().filterAttr;
                this._sourceFilterIncomplete = false;
                if (!filterAttr) return undefined;
                try {
                    var parsed = typeof filterAttr === "string" ? JSON.parse(filterAttr) : filterAttr;
                    return this.resolveSourceFilterValue(parsed);
                } catch (error) {
                    console.warn("[prm-choice-field] Invalid prm:reference-filter", error);
                    return undefined;
                }
            },
            getSourceOptionLabel: function(row, displayField) {
                if (!row) return "";
                var value = displayField ? (row.display || row[displayField]) : (row.display || row.title || row.name || row.number_ref || row.id);
                if (value && typeof value === "object") {
                    value = value.display || value.title || value.name || value.number_ref || value.id || JSON.stringify(value);
                }
                if (value === null || value === undefined) return "";
                return String(value);
            },
            loadDynamicOptions: function() {
                var self = this;
                var config = this.getSourceConfig();
                if (!config.table) return;

                var gate = getGate();
                if (!gate || !gate.api || !gate.api.record) return;

                var filter = self.getSourceFilter();
                if (self._sourceFilterIncomplete) {
                    self.dynamicOptions = [];
                    return;
                }

                self.loadingDynamic = true;
                gate.api.record.query({
                    table: config.table,
                    query: filter,
                    page: 1,
                    perPage: 100,
                    referenceMode: "display"
                }).then(function(result) {
                    var rows = (result && (result.data || result.records)) || [];
                    self.dynamicOptions = rows.map(function(row) {
                        return {
                            value: (config.valueField && row[config.valueField] !== undefined ? row[config.valueField] : row.id),
                            label: self.getSourceOptionLabel(row, config.display)
                        };
                    });
                }).catch(function() {
                    self.dynamicOptions = [];
                }).finally(function() {
                    self.loadingDynamic = false;
                });
            },
            slotOptions: function() {
                var nodes = this.$slots && this.$slots.default ? this.$slots.default() : [];
                var options = [];
                (nodes || []).forEach(function(vnode) {
                    var tag = getPrmTagFromVNode(vnode);
                    var props = vnode && vnode.props ? vnode.props : {};
                    if (tag !== "prm-choice-field-item" && tag !== "prm-choice-item") return;
                    var value = props.value === undefined || props.value === null ? "" : String(props.value);
                    var label = props.label === undefined || props.label === null ? value : String(props.label);
                    options.push({ value: value, label: resolveLabel(label) });
                });
                return options;
            },
            openDropdown: function() {
                if (this.isDisabled || this.isReadonly) return;
                this.searchQuery = this.selectedOption ? this.selectedOption.label : "";
                this.showDropdown = true;
                this.activeIndex = this.filteredOptions.length ? 0 : -1;
            },
            onSearchInput: function(event) {
                this.searchQuery = event.target.value;
                this.showDropdown = true;
                this.activeIndex = this.filteredOptions.length ? 0 : -1;
            },
            onDocumentClick: function(event) {
                if (!this.$el || this.$el.contains(event.target)) return;
                this.showDropdown = false;
            },
            selectOption: function(opt) {
                if (!this.isInteractive() || !opt) return;
                this.fieldValue = opt.value;
                this.searchQuery = opt.label;
                this.showDropdown = false;
                if (this.formStore.validateField) this.formStore.validateField(this.name);
            },
            clearSelection: function() {
                if (!this.isInteractive()) return;
                this.fieldValue = null;
                this.searchQuery = "";
                this.showDropdown = false;
            },
            onKeydown: function(event) {
                if (!this.isInteractive()) {
                    this.showDropdown = false;
                    return;
                }
                if (event.key === "ArrowDown") {
                    event.preventDefault();
                    this.showDropdown = true;
                    this.activeIndex = Math.min(this.activeIndex + 1, this.filteredOptions.length - 1);
                } else if (event.key === "ArrowUp") {
                    event.preventDefault();
                    this.activeIndex = Math.max(this.activeIndex - 1, 0);
                } else if (event.key === "Enter") {
                    if (this.showDropdown) {
                        event.preventDefault();
                        this.selectOption(this.filteredOptions[this.activeIndex]);
                    }
                } else if (event.key === "Escape") {
                    this.showDropdown = false;
                }
            }
        },
        template: fieldWrapperTemplate(
            '<div class="prm-field__choice relative">' +
                '<div class="relative">' +
                    '<input type="text" class="prm-field__input pr-20" :id="name" ' +
                    ':value="displayText" @focus="openDropdown" @input="onSearchInput" @keydown="onKeydown" @blur="formStore.validateField && formStore.validateField(name)" ' +
                    ':placeholder="resolvedPlaceholder || choiceSelectLabel" :readonly="isReadonly" :disabled="isDisabled" role="combobox" :aria-expanded="showDropdown" />' +
                    '<button v-if="fieldValue && !isDisabled && !isReadonly" type="button" class="prm-field__clear absolute right-9 top-1/2 -translate-y-1/2 rounded px-2 text-[var(--prm-text-secondary,#6b7280)] hover:text-[var(--prm-text,#111827)]" :aria-label="clearLabel()" @click="clearSelection" @keydown.enter.prevent="clearSelection" @keydown.space.prevent="clearSelection" :disabled="isDisabled || isReadonly">&times;</button>' +
                    '<svg class="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--prm-text-secondary,#6b7280)]" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true"><path fill-rule="evenodd" d="M5.22 8.22a.75.75 0 0 1 1.06 0L10 11.94l3.72-3.72a.75.75 0 1 1 1.06 1.06l-4.25 4.25a.75.75 0 0 1-1.06 0L5.22 9.28a.75.75 0 0 1 0-1.06Z" clip-rule="evenodd"></path></svg>' +
                '</div>' +
                '<div v-if="showDropdown" class="prm-field__choice-dropdown absolute z-30 mt-1 max-h-60 w-full overflow-auto rounded-prm border border-prm-border bg-prm-surface shadow-prm transition-all">' +
                    '<button v-for="(opt, idx) in filteredOptions" :key="opt.value" type="button" class="block w-full px-3 py-2 text-left text-sm hover:bg-[var(--prm-surface-raised,#f1f5f9)]" :class="{ &quot;bg-[var(--prm-surface-raised,#f1f5f9)]&quot;: idx === activeIndex }" @mousedown.prevent="selectOption(opt)">{{ opt.label }}</button>' +
                    '<div v-if="loadingDynamic && !filteredOptions.length" class="px-3 py-2 text-sm text-[var(--prm-text-secondary,#6b7280)]">Loading...</div>' +
                    '<div v-else-if="!filteredOptions.length" class="px-3 py-2 text-sm text-[var(--prm-text-secondary,#6b7280)]">No results</div>' +
                '</div>' +
            '</div>'
        )
    };

    window.__prmComponents["prm-choice-field-item"] = {
        props: {
            label: { type: String, default: "" },
            value: { type: String, default: "" }
        },
        template: '<span style="display:none"></span>'
    };

    // ══════════════════════════════════════════════════
    // prm-reference-field
    // ══════════════════════════════════════════════════
    window.__prmComponents["prm-reference-field"] = {
        mixins: [fieldMixin],
        data: function() {
            return {
                searchQuery: "",
                referenceOptions: [],
                searching: false,
                showDropdown: false,
                activeIndex: -1,
                searchTimer: null,
                lookupError: ""
            };
        },
        computed: {
            displayValue: function() {
                if (!this.fieldValue) return "";
                var currentValue = String(this.fieldValue);
                var opt = this.referenceOptions.find(function(o) { return String(o.value) === currentValue; });
                return opt ? opt.label : this.searchQuery || this.fieldValue;
            },
            referencePlaceholder: function() {
                return this.resolvedPlaceholder || runtimeText("forms.reference.search", "Search...");
            }
        },
        mounted: function() {
            document.addEventListener("click", this.onDocumentClick);
            if (this.fieldValue) this.resolveDisplayValue();
        },
        watch: {
            fieldValue: function(value) {
                if (!value) {
                    this.searchQuery = "";
                    this.referenceOptions = [];
                    return;
                }

                var currentValue = String(value);
                var hasOption = this.referenceOptions.some(function(option) { return String(option.value) === currentValue; });
                if (!hasOption) this.resolveDisplayValue();
            }
        },
        beforeUnmount: function() {
            document.removeEventListener("click", this.onDocumentClick);
            if (this.searchTimer) clearTimeout(this.searchTimer);
        },
        methods: {
            getReferenceConfig: function() {
                return {
                    table: this.$attrs["prm:source"] || this.$attrs["prm-source"] || this.source || "",
                    display: this.$attrs["prm:display"] || this.$attrs["prm-display"] || this.display || "name",
                    filterAttr: this.$attrs["prm:reference-filter"] || this.$attrs["prm-reference-filter"] || ""
                };
            },
            resolveFilterValue: function(value) {
                if (typeof value === "string" && value.indexOf("$form.") === 0) {
                    var resolved = this.formStore.getValue(value.slice(6));
                    if (resolved === undefined || resolved === null || resolved === "") this._referenceFilterIncomplete = true;
                    return resolved;
                }
                if (Array.isArray(value)) return value.map(this.resolveFilterValue.bind(this));
                if (value && typeof value === "object") {
                    var output = {};
                    Object.keys(value).forEach(function(key) { output[key] = this.resolveFilterValue(value[key]); }.bind(this));
                    return output;
                }
                return value;
            },
            // Sets this._referenceFilterIncomplete when the filter references a
            // $form.<field> value that isn't filled in yet — performSearch checks
            // it and skips querying rather than sending a condition with a
            // missing value (backend rejects it, and a filter that can't be
            // fully applied should show no options, not all of them).
            getReferenceFilter: function() {
                var filterAttr = this.getReferenceConfig().filterAttr;
                this._referenceFilterIncomplete = false;
                if (!filterAttr) return null;
                try {
                    var parsed = typeof filterAttr === "string" ? JSON.parse(filterAttr) : filterAttr;
                    return this.resolveFilterValue(parsed);
                } catch (error) {
                    console.warn("[prm-reference-field] Invalid prm:reference-filter", error);
                    return null;
                }
            },
            getReferenceLabel: function(row, displayField) {
                if (!row) return "";
                var value = row.display || row[displayField] || row.title || row.name || row.number_ref || row.id;
                if (value && typeof value === "object") {
                    value = value.display || value.title || value.name || value.number_ref || value.id || JSON.stringify(value);
                }
                if (value === null || value === undefined) return "";
                // PRIM-004: the display value can itself be a translation key (same convention
                // used for CHOICE options a few lines below via resolveLabel) — without this the
                // field showed the raw key instead of the translated text.
                return resolveLabel(String(value));
            },
            buildQueryFilter: function(queryText) {
                var display = this.getReferenceConfig().display;
                var referenceFilter = this.getReferenceFilter();
                var searchFilter = queryText ? {
                    type: "condition",
                    field: display,
                    operator: "contains",
                    value: queryText
                } : null;
                return referenceFilter && searchFilter ? {
                    type: "logical",
                    operator: "and",
                    conditions: [referenceFilter, searchFilter]
                } : (referenceFilter || searchFilter || undefined);
            },
            isInteractive: function() {
                return !this.isDisabled && !this.isReadonly;
            },
            onSearch: function() {
                if (!this.isInteractive()) {
                    this.showDropdown = false;
                    return;
                }
                if (this.searchTimer) clearTimeout(this.searchTimer);
                this.searchTimer = setTimeout(this.performSearch, 400);
            },
            performSearch: function() {
                var self = this;
                if (!self.isInteractive()) {
                    self.referenceOptions = [];
                    self.searching = false;
                    self.showDropdown = false;
                    return;
                }
                var config = this.getReferenceConfig();

                if (!config.table) {
                    self.referenceOptions = [];
                    self.searching = false;
                    return;
                }

                self.searching = true;
                self.showDropdown = true;

                var gate = getGate();
                if (!gate || !gate.api || !gate.api.record) {
                    self.referenceOptions = [];
                    self.searching = false;
                    return;
                }

                var queryFilter = self.buildQueryFilter(self.searchQuery);
                if (self._referenceFilterIncomplete) {
                    self.referenceOptions = [];
                    self.activeIndex = -1;
                    self.searching = false;
                    return;
                }

                // No search text yet (e.g. right after focus) — show a small initial
                // batch instead of an empty "No results" state; once the user types,
                // this becomes a real filtered search with a higher cap. Using
                // page/perPage here, not "limit": the backend's generic records query
                // (RecordsQueryService) only ever reads params[:per_page] — a bare
                // "limit" is silently ignored and it falls back to returning up to
                // DEFAULT_PER_PAGE rows regardless of what's requested.
                self.lookupError = "";
                var portalFormId = self.formStore && self.formStore.servicePortalFormId;
                var lookup = portalFormId
                    ? (gate.api.servicePortal && gate.api.servicePortal.lookupReference
                        ? gate.api.servicePortal.lookupReference({ formId: portalFormId, column: self.name, q: self.searchQuery })
                        : Promise.reject(new Error(runtimeText("forms.reference.unavailable", "Portal lookup unavailable"))))
                    : gate.api.record.query({
                    table: config.table,
                    query: queryFilter,
                    page: 1,
                    perPage: self.searchQuery ? 20 : 10,
                    referenceMode: "display"
                });
                lookup.then(function(result) {
                    var rows = Array.isArray(result) ? result : (result && (result.data || result.records)) || [];
                    self.referenceOptions = rows.map(function(row) {
                        return { value: row.id, label: self.getReferenceLabel(row, config.display) };
                    });
                    self.activeIndex = self.referenceOptions.length ? 0 : -1;
                    self.searching = false;
                }).catch(function() {
                    self.referenceOptions = [];
                    self.activeIndex = -1;
                    self.searching = false;
                    self.lookupError = gate.i18n && gate.i18n.t
                        ? gate.i18n.t("forms.reference.lookup_failed", {}, "Could not load options. Try again.")
                        : runtimeText("forms.reference.lookup_failed", "Could not load options. Try again.");
                });
            },
            resolveDisplayValue: function() {
                var self = this;
                var config = this.getReferenceConfig();
                var gate = getGate();
                if (!config.table || !this.fieldValue || !gate || !gate.api || !gate.api.record) return;
                var portalFormId = this.formStore && this.formStore.servicePortalFormId;
                var lookup = portalFormId
                    ? (gate.api.servicePortal && gate.api.servicePortal.lookupReference
                        ? gate.api.servicePortal.lookupReference({ formId: portalFormId, column: this.name, q: String(this.fieldValue) })
                        : Promise.reject(new Error(runtimeText("forms.reference.unavailable", "Portal lookup unavailable"))))
                    : gate.api.record.query({
                    table: config.table,
                    query: { type: "condition", field: "id", operator: "eq", value: this.fieldValue },
                    limit: 1,
                    referenceMode: "display"
                });
                lookup.then(function(result) {
                    var rows = Array.isArray(result) ? result : (result && (result.data || result.records)) || [];
                    var row = rows[0];
                    if (!row) return;
                    var option = { value: row.id, label: self.getReferenceLabel(row, config.display) };
                    self.referenceOptions = [option];
                    self.searchQuery = option.label;
                }).catch(function() {
                    self.lookupError = gate.i18n && gate.i18n.t
                        ? gate.i18n.t("forms.reference.lookup_failed", {}, "Could not load options. Try again.")
                        : runtimeText("forms.reference.lookup_failed", "Could not load options. Try again.");
                });
            },
            onFocus: function() {
                if (!this.isInteractive()) {
                    this.showDropdown = false;
                    return;
                }
                this.showDropdown = true;
                if (!this.searchQuery && this.displayValue) this.searchQuery = this.displayValue;
                if (!this.searchQuery && !this.referenceOptions.length) this.performSearch();
            },
            onDocumentClick: function(event) {
                if (!this.$el || this.$el.contains(event.target)) return;
                this.showDropdown = false;
            },
            onKeydown: function(event) {
                if (!this.isInteractive()) {
                    this.showDropdown = false;
                    return;
                }
                if (event.key === "ArrowDown") {
                    event.preventDefault();
                    this.showDropdown = true;
                    this.activeIndex = Math.min(this.activeIndex + 1, this.referenceOptions.length - 1);
                } else if (event.key === "ArrowUp") {
                    event.preventDefault();
                    this.activeIndex = Math.max(this.activeIndex - 1, 0);
                } else if (event.key === "Enter") {
                    if (this.showDropdown) {
                        event.preventDefault();
                        this.selectOption(this.referenceOptions[this.activeIndex]);
                    }
                } else if (event.key === "Escape") {
                    this.showDropdown = false;
                }
            },
            selectOption: function(opt) {
                if (!this.isInteractive() || !opt) return;
                this.fieldValue = opt.value;
                this.searchQuery = opt.label;
                this.showDropdown = false;
                if (this.formStore.validateField) this.formStore.validateField(this.name);
            },
            clearSelection: function() {
                if (!this.isInteractive()) return;
                this.fieldValue = null;
                this.searchQuery = "";
                this.referenceOptions = [];
                this.showDropdown = false;
            }
        },
        template: fieldWrapperTemplate(
            '<div class="prm-field__reference relative">' +
                '<div class="prm-field__reference-input-wrap relative">' +
                    '<input type="text" class="prm-field__input pr-20" :id="name" ' +
                    'v-model="searchQuery" @input="onSearch" @focus="onFocus" @keydown="onKeydown" @blur="formStore.validateField && formStore.validateField(name)" ' +
                    ':placeholder="referencePlaceholder" :readonly="isReadonly" :disabled="isDisabled" role="combobox" :aria-expanded="showDropdown" />' +
                    '<span v-if="searching" class="absolute right-10 top-1/2 h-4 w-4 -translate-y-1/2 rounded-full border-2 border-slate-300 border-t-prm-primary animate-spin"></span>' +
                    '<button v-if="fieldValue && !isDisabled && !isReadonly" type="button" class="prm-field__clear prm-field__reference-clear absolute right-2 top-1/2 -translate-y-1/2 rounded px-2 text-[var(--prm-text-secondary,#6b7280)] hover:text-[var(--prm-text,#111827)]" :aria-label="clearLabel()" @click="clearSelection" @keydown.enter.prevent="clearSelection" @keydown.space.prevent="clearSelection" :disabled="isDisabled || isReadonly">&times;</button>' +
                '</div>' +
                '<div v-if="lookupError" class="prm-field__error" role="alert">{{ lookupError }}</div>' +
                '<div v-if="isInteractive() && showDropdown && (referenceOptions.length || searching)" class="prm-field__reference-dropdown absolute z-30 mt-1 max-h-60 w-full overflow-auto rounded-prm border border-prm-border bg-prm-surface shadow-prm transition-all">' +
                    '<button v-for="(opt, idx) in referenceOptions" :key="opt.value" type="button" class="prm-field__reference-option block w-full px-3 py-2 text-left text-sm hover:bg-[var(--prm-surface-raised,#f1f5f9)]" :class="{ &quot;bg-[var(--prm-surface-raised,#f1f5f9)]&quot;: idx === activeIndex }" @mousedown.prevent="selectOption(opt)">{{ opt.label }}</button>' +
                    '<div v-if="searching && !referenceOptions.length" class="prm-field__reference-loading flex items-center gap-2 px-3 py-2 text-sm text-slate-500"><span class="h-4 w-4 rounded-full border-2 border-slate-300 border-t-prm-primary animate-spin"></span>Loading...</div>' +
                '</div>' +
            '</div>'
        )
    };

    // ══════════════════════════════════════════════════
    // prm-richtext-field
    // ══════════════════════════════════════════════════
    window.__prmComponents["prm-richtext-field"] = {
        mixins: [fieldMixin],
        data: function() {
            return { savedSelection: null };
        },
        mounted: function() {
            this.applyValueToEditor();
        },
        watch: {
            fieldValue: function() {
                this.applyValueToEditor();
            }
        },
        methods: {
            toolbarLabel: function(key, fallback) {
                var gate = getGate();
                var translationKey = "forms.richtext." + key;
                return gate && gate.i18n && gate.i18n.has && gate.i18n.has(translationKey)
                    ? gate.i18n.t(translationKey, {}, fallback)
                    : fallback;
            },
            applyValueToEditor: function() {
                var el = this.$refs.editor;
                if (!el || document.activeElement === el) return;

                var value = this.fieldValue || "";
                if (el.innerHTML !== value) {
                    el.innerHTML = value;
                }
            },
            saveSelection: function() {
                var el = this.$refs.editor;
                var selection = window.getSelection ? window.getSelection() : null;
                if (!el || !selection || selection.rangeCount === 0) return;

                var range = selection.getRangeAt(0);
                if (el.contains(range.commonAncestorContainer)) {
                    this.savedSelection = range.cloneRange();
                }
            },
            restoreSelection: function() {
                var el = this.$refs.editor;
                if (!el) return;

                el.focus();
                if (!this.savedSelection || !window.getSelection) return;

                var selection = window.getSelection();
                selection.removeAllRanges();
                selection.addRange(this.savedSelection);
            },
            selectedHtml: function() {
                var selection = window.getSelection ? window.getSelection() : null;
                if (!selection || selection.rangeCount === 0 || selection.isCollapsed) return "";
                var container = document.createElement("div");
                container.appendChild(selection.getRangeAt(0).cloneContents());
                return container.innerHTML;
            },
            selectedText: function() {
                var selection = window.getSelection ? window.getSelection() : null;
                return selection ? String(selection.toString() || "") : "";
            },
            insertHtml: function(html) {
                if (!document.execCommand("insertHTML", false, html)) {
                    var selection = window.getSelection ? window.getSelection() : null;
                    if (!selection || selection.rangeCount === 0) return;
                    var range = selection.getRangeAt(0);
                    var fragment = range.createContextualFragment(html);
                    range.deleteContents();
                    range.insertNode(fragment);
                    range.collapse(false);
                    selection.removeAllRanges();
                    selection.addRange(range);
                }
            },
            fallbackCommand: function(command, value) {
                var html = this.selectedHtml();
                var text = this.selectedText();
                var content = html || text || "&nbsp;";
                if (command === "insertUnorderedList") this.insertHtml("<ul><li>" + content + "</li></ul>");
                if (command === "insertOrderedList") this.insertHtml("<ol><li>" + content + "</li></ol>");
                if (command === "formatBlock") this.insertHtml("<" + value + ">" + content + "</" + value + ">");
                if (command === "createLink") this.insertHtml('<a href="' + String(value || "").replace(/"/g, "&quot;") + '" target="_blank" rel="noopener noreferrer">' + (content === "&nbsp;" ? value : content) + "</a>");
            },
            exec: function(command, value) {
                if (this.isDisabled || this.isReadonly) return;
                this.restoreSelection();
                var before = this.$refs.editor ? this.$refs.editor.innerHTML : "";
                var ok = document.execCommand(command, false, value || null);
                if (!ok || (this.$refs.editor && this.$refs.editor.innerHTML === before && ["insertUnorderedList", "insertOrderedList", "formatBlock", "createLink"].indexOf(command) >= 0)) {
                    this.fallbackCommand(command, value);
                }
                this.syncValue();
                this.saveSelection();
            },
            createLink: function() {
                this.restoreSelection();
                this.saveSelection();
                var url = window.prompt(runtimeText("forms.richtext.url_prompt", "URL"));
                if (url) this.exec("createLink", url);
            },
            syncValue: function() {
                var el = this.$refs.editor;
                if (!el) return;
                var value = el.innerHTML;
                var maxLength = parseInt(this.maxlength || "", 10);
                if (!isNaN(maxLength) && el.innerText.length > maxLength) {
                    el.innerText = el.innerText.slice(0, maxLength);
                    value = el.innerHTML;
                }
                this.fieldValue = value;
            },
            onPaste: function(event) {
                if (this.isDisabled || this.isReadonly) return;
                event.preventDefault();
                var text = (event.clipboardData || window.clipboardData).getData("text/plain");
                document.execCommand("insertText", false, text);
                this.syncValue();
                this.saveSelection();
            },
            onInput: function() {
                this.syncValue();
                this.saveSelection();
            }
        },
        template: fieldWrapperTemplate(
            '<div class="prm-field__richtext-wrap rounded-prm border bg-prm-surface focus-within:ring-2 focus-within:ring-prm-primary/20">' +
                '<div class="prm-field__richtext-toolbar flex flex-wrap gap-1 border-b border-prm-border p-2">' +
                    '<button type="button" class="rounded px-2 py-1 text-sm font-bold hover:bg-[var(--prm-surface-raised,#f1f5f9)]" :aria-label="toolbarLabel(&quot;bold&quot;, &quot;Bold&quot;)" :title="toolbarLabel(&quot;bold&quot;, &quot;Bold&quot;)" @mousedown.prevent="restoreSelection" @click="exec(&quot;bold&quot;)">B</button>' +
                    '<button type="button" class="rounded px-2 py-1 text-sm italic hover:bg-[var(--prm-surface-raised,#f1f5f9)]" :aria-label="toolbarLabel(&quot;italic&quot;, &quot;Italic&quot;)" :title="toolbarLabel(&quot;italic&quot;, &quot;Italic&quot;)" @mousedown.prevent="restoreSelection" @click="exec(&quot;italic&quot;)">I</button>' +
                    '<button type="button" class="rounded px-2 py-1 text-sm underline hover:bg-[var(--prm-surface-raised,#f1f5f9)]" :aria-label="toolbarLabel(&quot;underline&quot;, &quot;Underline&quot;)" :title="toolbarLabel(&quot;underline&quot;, &quot;Underline&quot;)" @mousedown.prevent="restoreSelection" @click="exec(&quot;underline&quot;)">U</button>' +
                    '<button type="button" class="rounded px-2 py-1 text-sm hover:bg-[var(--prm-surface-raised,#f1f5f9)]" :aria-label="toolbarLabel(&quot;bullet_list&quot;, &quot;Bulleted list&quot;)" :title="toolbarLabel(&quot;bullet_list&quot;, &quot;Bulleted list&quot;)" @mousedown.prevent="restoreSelection" @click="exec(&quot;insertUnorderedList&quot;)">{{ toolbarLabel("bullet_list", "Bulleted list") }}</button>' +
                    '<button type="button" class="rounded px-2 py-1 text-sm hover:bg-[var(--prm-surface-raised,#f1f5f9)]" :aria-label="toolbarLabel(&quot;ordered_list&quot;, &quot;Numbered list&quot;)" :title="toolbarLabel(&quot;ordered_list&quot;, &quot;Numbered list&quot;)" @mousedown.prevent="restoreSelection" @click="exec(&quot;insertOrderedList&quot;)">{{ toolbarLabel("ordered_list", "Numbered list") }}</button>' +
                    '<button type="button" class="rounded px-2 py-1 text-sm hover:bg-[var(--prm-surface-raised,#f1f5f9)]" :aria-label="toolbarLabel(&quot;link&quot;, &quot;Link&quot;)" :title="toolbarLabel(&quot;link&quot;, &quot;Link&quot;)" @mousedown.prevent="restoreSelection" @click="createLink">{{ toolbarLabel("link", "Link") }}</button>' +
                    '<button type="button" class="rounded px-2 py-1 text-sm hover:bg-[var(--prm-surface-raised,#f1f5f9)]" :aria-label="toolbarLabel(&quot;heading1&quot;, &quot;Heading 1&quot;)" :title="toolbarLabel(&quot;heading1&quot;, &quot;Heading 1&quot;)" @mousedown.prevent="restoreSelection" @click="exec(&quot;formatBlock&quot;, &quot;h1&quot;)">H1</button>' +
                    '<button type="button" class="rounded px-2 py-1 text-sm hover:bg-[var(--prm-surface-raised,#f1f5f9)]" :aria-label="toolbarLabel(&quot;heading2&quot;, &quot;Heading 2&quot;)" :title="toolbarLabel(&quot;heading2&quot;, &quot;Heading 2&quot;)" @mousedown.prevent="restoreSelection" @click="exec(&quot;formatBlock&quot;, &quot;h2&quot;)">H2</button>' +
                    '<button type="button" class="rounded px-2 py-1 text-sm hover:bg-[var(--prm-surface-raised,#f1f5f9)]" :aria-label="toolbarLabel(&quot;heading3&quot;, &quot;Heading 3&quot;)" :title="toolbarLabel(&quot;heading3&quot;, &quot;Heading 3&quot;)" @mousedown.prevent="restoreSelection" @click="exec(&quot;formatBlock&quot;, &quot;h3&quot;)">H3</button>' +
                '</div>' +
                '<div ref="editor" class="prm-field__richtext min-h-[120px] max-h-[420px] overflow-auto p-3 outline-none" :id="name" :contenteditable="isDisabled || isReadonly ? &quot;false&quot; : &quot;true&quot;" @input="onInput" @keyup="saveSelection" @mouseup="saveSelection" @paste="onPaste" @blur="formStore.validateField && formStore.validateField(name)"></div>' +
            '</div>'
        )
    };

    window.__prmComponents["prm-attachment-field"] = {
        mixins: [fieldMixin],
        data: function() {
            return { files: [], dragOver: false };
        },
        computed: {
            allowMultiple: function() { return this.multiple === true || this.multiple === "true" || this.multiple === "multiple" || this.$attrs.multiple !== undefined; },
            dropOrBrowseLabel: function() { return runtimeText("forms.attachment.drop_or_browse", "Drop files here or browse"); },
            removeFileLabel: function() { return runtimeText("forms.attachment.remove", "Remove file"); }
        },
        methods: {
            formatSize: function(size) {
                if (!size && size !== 0) return "";
                if (size < 1024 * 1024) return Math.max(1, Math.round(size / 1024)) + " KB";
                return (size / (1024 * 1024)).toFixed(1) + " MB";
            },
            previewUrl: function(file) {
                if (!file || !file.type || file.type.indexOf("image/") !== 0) return "";
                if (!file.__prmPreviewUrl) file.__prmPreviewUrl = URL.createObjectURL(file);
                return file.__prmPreviewUrl;
            },
            setFiles: function(fileList) {
                var nextFiles = Array.from(fileList || []);
                this.files = this.allowMultiple ? nextFiles : nextFiles.slice(0, 1);
                this.fieldValue = this.allowMultiple ? this.files : (this.files[0] || null);
                if (this.formStore.validateField) this.formStore.validateField(this.name);
            },
            onFileChange: function(e) { this.setFiles(e.target.files); },
            onDrop: function(event) {
                event.preventDefault();
                this.dragOver = false;
                if (this.isDisabled || this.isReadonly) return;
                this.setFiles(event.dataTransfer.files);
            },
            clearFile: function(index) {
                this.files.splice(index, 1);
                this.fieldValue = this.allowMultiple ? this.files : (this.files[0] || null);
            }
        },
        template: fieldWrapperTemplate(
            '<div class="prm-field__attachment">' +
                '<label class="flex cursor-pointer flex-col items-center justify-center rounded-prm border-2 border-dashed p-5 text-center transition-colors" :class="dragOver ? &quot;border-prm-primary bg-[var(--prm-surface-raised,#f1f5f9)]&quot; : &quot;border-prm-border&quot;" @dragover.prevent="dragOver = true" @dragleave.prevent="dragOver = false" @drop="onDrop">' +
                    '<input type="file" class="sr-only" :id="name" @change="onFileChange" :accept="accept || undefined" :multiple="allowMultiple" :disabled="isDisabled || isReadonly" />' +
                    '<span class="text-sm font-medium text-[var(--prm-text-secondary,#6b7280)]">{{ dropOrBrowseLabel }}</span>' +
                    '<span class="mt-1 text-xs text-slate-500" v-if="accept">{{ accept }}</span>' +
                '</label>' +
                '<div v-if="files.length" class="mt-3 space-y-2">' +
                    '<div v-for="(file, idx) in files" :key="file.name + idx" class="flex items-center gap-3 rounded-prm border border-prm-border p-2">' +
                        '<img v-if="previewUrl(file)" :src="previewUrl(file)" class="h-12 w-12 rounded object-cover" />' +
                        '<div class="min-w-0 flex-1"><div class="truncate text-sm font-medium">{{ file.name }}</div><div class="text-xs text-slate-500">{{ formatSize(file.size) }}</div></div>' +
                        '<button type="button" class="rounded px-2 text-[var(--prm-text-secondary,#6b7280)] hover:text-[var(--prm-text,#111827)]" :aria-label="removeFileLabel" @click="clearFile(idx)">&times;</button>' +
                    '</div>' +
                '</div>' +
            '</div>'
        )
    };

    // ══════════════════════════════════════════════════
    // prm-color-field
    // ══════════════════════════════════════════════════
    window.__prmComponents["prm-color-field"] = {
        mixins: [fieldMixin],
        template: fieldWrapperTemplate(
            '<div class="prm-field__color-wrap">' +
                '<input type="color" class="prm-field__color-input" :id="name" ' +
                'v-model="fieldValue" :disabled="isDisabled || isReadonly" />' +
                '<span class="prm-field__color-value">{{ fieldValue || "#000000" }}</span>' +
            '</div>'
        )
    };

    // ══════════════════════════════════════════════════
    // prm-form-summary (review step widget)
    // ══════════════════════════════════════════════════
    window.__prmComponents["prm-form-summary"] = {
        inject: ["formStore"],
        computed: {
            summaryFields: function() {
                var store = this.formStore;
                var result = [];
                store.__fieldOrder.forEach(function(name) {
                    var def = store.__fieldDefs[name];
                    var state = store.__getFieldState(name);
                    if (!state.visible) return;
                    var value = store.getValue(name);
                    if (value === null || value === undefined || value === "") return;

                    result.push({
                        name: name,
                        label: resolveLabel(state.label || def.label || name),
                        value: typeof value === "boolean" ? (value ? runtimeText("forms.boolean.yes", "Yes") : runtimeText("forms.boolean.no", "No")) : String(value),
                        type: def.type
                    });
                });
                return result;
            }
        },
        methods: { emptyLabel: function() { return runtimeText("forms.summary.empty", "No data to display"); } },
        template: '<div class="prm-form-summary">' +
            '<div v-for="field in summaryFields" :key="field.name" class="prm-form-summary__item">' +
                '<span class="prm-form-summary__label">{{ field.label }}</span>' +
                '<span class="prm-form-summary__value">{{ field.value }}</span>' +
            '</div>' +
            '<div v-if="summaryFields.length === 0" class="prm-form-summary__empty">' +
                '{{ emptyLabel }}' +
            '</div>' +
        '</div>'
    };

    // ══════════════════════════════════════════════════
    // prm-section
    // ══════════════════════════════════════════════════
    window.__prmComponents["prm-section"] = {
        props: {
            title: { type: String, default: "" },
            collapsible: { type: [String, Boolean], default: false }
        },
        data: function() {
            return { collapsed: false };
        },
        computed: {
            isCollapsible: function() { return this.collapsible === true || this.collapsible === "true"; },
            resolvedTitle: function() { return resolveLabel(this.title); },
            sectionClasses: function() { return { "prm-section--collapsed": this.collapsed }; }
        },
        template: '<fieldset class="prm-section" :class="sectionClasses">' +
            '<legend v-if="resolvedTitle" class="prm-section__legend" @click="isCollapsible && (collapsed = !collapsed)">' +
                '<span class="prm-section__title">{{ resolvedTitle }}</span>' +
                '<span v-if="isCollapsible" class="prm-section__toggle">{{ collapsed ? "▸" : "▾" }}</span>' +
            '</legend>' +
            '<div v-show="!collapsed" class="prm-section__body"><slot /></div>' +
        '</fieldset>'
    };

    // ══════════════════════════════════════════════════
    // prm-row
    // ══════════════════════════════════════════════════
    window.__prmComponents["prm-row"] = {
        template: '<div class="prm-row"><slot /></div>'
    };

    // ══════════════════════════════════════════════════
    // prm-step
    // ══════════════════════════════════════════════════
    window.__prmComponents["prm-step"] = {
        inject: ["formStore"],
        props: {
            title: { type: String, default: "" },
            icon: { type: String, default: "" },
            stepIndex: { type: [String, Number], default: 0 },
            "step-index": { type: [String, Number], default: 0 }
        },
        computed: {
            resolvedStepIndex: function() {
                var parsed = Number(this.stepIndex !== undefined && this.stepIndex !== null && this.stepIndex !== "" ? this.stepIndex : this["step-index"]);
                return Number.isFinite(parsed) ? parsed : 0;
            },
            isActive: function() {
                return this.formStore.getCurrentStep() === this.resolvedStepIndex;
            },
            stepClasses: function() { return { "prm-step--active": this.isActive }; }
        },
        template: '<div class="prm-step" :class="stepClasses" ' +
            'v-show="isActive" data-prm-step :data-prm-step-index="resolvedStepIndex">' +
            '<slot></slot>' +
        '</div>'
    };

    // ══════════════════════════════════════════════════
    // prm-submit
    // ══════════════════════════════════════════════════
    window.__prmComponents["prm-submit"] = {
        inject: ["formStore"],
        props: {
            label: { type: String, default: "" },
            "update-label": { type: String, default: "" },
            updateLabel: { type: String, default: "" },
            "loading-label": { type: String, default: "" },
            loadingLabel: { type: String, default: "" },
            "updating-label": { type: String, default: "" },
            updatingLabel: { type: String, default: "" }
        },
        computed: {
            // A3: also disabled while the update-mode prefill is loading -- submitting mid-load
            // would send whatever the user had already typed over what's about to be replaced.
            isSubmitting: function() { return this.formStore.__state.submitting || this.formStore.__state.loading; },
            isLastStep: function() {
                if (!this.formStore.__isMultiStep) return true;
                return this.formStore.getCurrentStep() >= this.formStore.getStepCount() - 1;
            },
            effectiveMode: function() {
                return this.formStore && this.formStore.__state && this.formStore.__state.effectiveMode || "create";
            },
            hasSerializerSubmitLabel: function() {
                return String(this.label || "").trim().toLowerCase() === "submit";
            },
            buttonLabel: function() {
                if (this.isSubmitting) {
                    if (this.effectiveMode === "update") {
                        return resolveLabel(this["updating-label"] || this.updatingLabel || this["loading-label"] || this.loadingLabel || runtimeText("forms.submit.updating", "Updating..."));
                    }
                    return resolveLabel(this["loading-label"] || this.loadingLabel || runtimeText("forms.submit.submitting", "Submitting..."));
                }
                if (this.effectiveMode === "update") {
                    var updateLabel = this["update-label"] || this.updateLabel;
                    if (updateLabel) return resolveLabel(updateLabel);
                    if (!this.label || this.hasSerializerSubmitLabel) return runtimeText("forms.submit.update", "Update");
                }
                return this.label && !this.hasSerializerSubmitLabel ? resolveLabel(this.label) : runtimeText("forms.submit.create", "Submit");
            }
        },
        methods: {
            onClick: function() {
                if (this.isSubmitting) return;
                if (this.formStore.submit) {
                    this.formStore.submit();
                }
            }
        },
        template: '<div class="prm-submit-wrap">' +
            '<div v-if="formStore.__isMultiStep" class="prm-step-nav">' +
                '<button v-if="formStore.getCurrentStep() > 0" type="button" ' +
                'class="prm-btn prm-btn--secondary" @click="formStore.previousStep()">' +
                    runtimeText("forms.step.previous", "Previous") +
                '</button>' +
                '<button v-if="!isLastStep" type="button" ' +
                'class="prm-btn prm-btn--primary" @click="formStore.nextStep()">' +
                    runtimeText("forms.step.next", "Next") +
                '</button>' +
            '</div>' +
            '<button v-if="isLastStep" type="button" class="prm-btn prm-btn--primary prm-btn--submit" ' +
            '@click="onClick" :disabled="isSubmitting">' +
                '{{ buttonLabel }}' +
            '</button>' +
        '</div>'
    };

    // ══════════════════════════════════════════════════
    // prm-form (root container)
    // ══════════════════════════════════════════════════
    window.__prmComponents["prm-form"] = {
        inject: {
            runtimeFormCatalog: { from: "formCatalog", default: null },
            runtimeGate: { from: "formGate", default: null },
            // A2 (design_form_update_mode_and_fluss_dispatch.md): same inject/fallback shape as
            // formCatalog above. The form's own template (authored in Service Builder) is static
            // HTML with no binding syntax -- an ancestor (the host page, which read the recordId
            // from wherever it lives: route param, Fluss context, etc.) is the only place that
            // can hand this in dynamically, which is exactly what provide/inject is for.
            runtimeRecordContext: { from: "formRecordContext", default: null },
            runtimeInviteContext: { from: "formInviteContext", default: null }
        },
        props: {
            "prm:target-table": { type: String, default: "" },
            "prm-target-table": { type: String, default: "" },
            targetTable: { type: String, default: "" },
            "target-table": { type: String, default: "" },
            "prm:on-submit": { type: String, default: "create" },
            "prm-on-submit": { type: String, default: "" },
            onSubmit: { type: String, default: "" },
            "on-submit": { type: String, default: "" },
            "prm:success-message": { type: String, default: "" },
            "prm-success-message": { type: String, default: "" },
            successMessage: { type: String, default: "" },
            "success-message": { type: String, default: "" },
            "prm:success-redirect": { type: String, default: "" },
            "prm-success-redirect": { type: String, default: "" },
            successRedirect: { type: String, default: "" },
            "success-redirect": { type: String, default: "" },
            "prm:record-id": { type: String, default: "" },
            "prm-record-id": { type: String, default: "" },
            recordId: { type: String, default: "" },
            "record-id": { type: String, default: "" },
            "prm:events": { type: String, default: "" },
            "prm-events": { type: String, default: "" },
            events: { type: String, default: "" }
        },
        data: function() {
            return {
                formStore: createPendingFormStore(),
                initialized: false
            };
        },
        provide: function() {
            return { formStore: this.formStore };
        },
        created: function() {
            // Form store is initialized in mounted after DOM is ready
        },
        mounted: async function() {
            var self = this;
            var definitionEl = buildFormDefinitionElement(this);
            var store = window.__prmCreateFormStore(definitionEl);

            // Wire submit
            store.submit = function() {
                return self.handleSubmit();
            };

            Object.keys(store).forEach(function(key) {
                self.formStore[key] = store[key];
            });
            this.formStore.__ready = true;
            this.formStore.__state.effectiveMode = this.resolveEffectiveMode();
            var servicePortalCatalog = this.resolveServicePortalCatalog();
            this.formStore.servicePortalFormId = servicePortalCatalog && servicePortalCatalog.id || "";

            // Expose to the scoped Gate used by this form, with global fallback for legacy forms.
            var activeGate = this.resolveGate();
            if (activeGate) {
                activeGate.form = this.formStore;
            }
            if (window.Gate && window.Gate !== activeGate) {
                window.Gate.form = this.formStore;
            }

            // A3 (design_form_update_mode_and_fluss_dispatch.md): awaited before onLoad fires,
            // so onLoad handlers (and the user) see the loaded record instead of a blank form
            // that then jumps to populated once the request lands.
            await this.loadRecordForPrefill();

            // Fire onLoad event
            this.$nextTick(function() {
                self.initialized = true;
                self.fireEvent("onLoad", [store]);
            });

            // Watch for changes and fire onChange
            var prevValues = Object.assign({}, store.__state.values);
            Vue.watch(function() { return store.__state.values; }, function(newVals) {
                Object.keys(newVals).forEach(function(field) {
                    if (newVals[field] !== prevValues[field]) {
                        self.fireEvent("onChange", [store, {
                            field: field,
                            value: newVals[field],
                            previousValue: prevValues[field]
                        }]);
                    }
                });
                prevValues = Object.assign({}, newVals);
            }, { deep: true });

            // Log rule warnings
            if (store.__ruleWarnings.length > 0) {
                store.__ruleWarnings.forEach(function(w) {
                    console.warn("[prm-form] Rule warning on field '" + w.field + "': " +
                        w.rule + " could not be parsed: " + w.raw);
                });
            }
        },
        methods: {
            resolveGate: function() {
                return this.runtimeGate || getGate();
            },
            readFormOption: function(names, fallback) {
                for (var i = 0; i < names.length; i++) {
                    var name = names[i];
                    var value = this.$attrs[name];
                    if (value === undefined || value === null || value === "") value = this[name];
                    if (value !== undefined && value !== null && value !== "") return value;
                }
                return fallback || "";
            },
            fireEvent: function(eventName, args) {
                // Look for events from prm:events binding
                var eventsAttr = this.readFormOption(["prm:events", "prm-events", "events"], "");
                if (eventsAttr && this.$parent && this.$parent[eventsAttr]) {
                    var handlers = this.$parent[eventsAttr];
                    if (handlers && typeof handlers[eventName] === "function") {
                        try {
                            return handlers[eventName].apply(null, args);
                        } catch(e) {
                            console.error("[prm-form] Event handler error (" + eventName + "):", e);
                        }
                    }
                }
                return undefined;
            },
            stepperStepClasses: function(idx) {
                return {
                    "prm-stepper__step--active": idx === this.formStore.getCurrentStep(),
                    "prm-stepper__step--completed": idx < this.formStore.getCurrentStep()
                };
            },
            stepTitle: function(step, idx) {
                var fallback = runtimeText("forms.step.default", "Step") + " " + (idx + 1);
                return resolveLabel(step && step.title ? step.title : fallback);
            },
            resolveCreatedRecordId: function(response) {
                if (!response) return "";
                if (response.id) return String(response.id);
                if (response.data && response.data.id) return String(response.data.id);
                if (response.record && response.record.id) return String(response.record.id);
                if (response.result && response.result.id) return String(response.result.id);
                return "";
            },
            resolveCreatedRecord: function(response) {
                if (!response) return null;
                if (response.data && typeof response.data === "object") return response.data;
                if (response.record && typeof response.record === "object") return response.record;
                if (response.result && typeof response.result === "object") return response.result;
                return typeof response === "object" ? response : null;
            },
            resolveServicePortalCatalog: function() {
                if (this.runtimeFormCatalog) return this.runtimeFormCatalog;
                if (this.formCatalog) return this.formCatalog;
                if (this.$root && this.$root.formCatalog) return this.$root.formCatalog;
                return null;
            },
            // A2 (design_form_update_mode_and_fluss_dispatch.md): { table, recordId } for the
            // record this form should load/update, resolved in order: injected by an ancestor
            // (the host page -- Fluss task page, Service Portal request-detail page, a page that
            // read a route/query param and passed it down as "formRecordContext") -> the same
            // static prm:record-id/prm:target-table attributes the form's own template can
            // already declare (kept so existing forms with a literal record id keep working).
            // The runtime itself never reads a route or query string -- see the design doc's
            // "runtime fica agnóstico de rota".
            resolveRecordContext: function() {
                var injected = this.runtimeRecordContext || this.formRecordContext ||
                    (this.$root && this.$root.formRecordContext) || null;
                if (injected && (injected.recordId || injected.table)) {
                    return {
                        table: injected.table || this.readFormOption(["prm:target-table", "prm-target-table", "target-table", "targetTable"], ""),
                        recordId: injected.recordId || ""
                    };
                }
                return {
                    table: this.readFormOption(["prm:target-table", "prm-target-table", "target-table", "targetTable"], ""),
                    recordId: this.readFormOption(["prm:record-id", "prm-record-id", "record-id", "recordId"], "")
                };
            },
            // A4 (design_form_update_mode_and_fluss_dispatch.md): the mode this submission
            // actually runs as, as opposed to the declared prm:on-submit. "update" (pure) is
            // always update, regardless of recordId -- A1's error path handles the missing-id
            // case, it must never silently fall back to create. "create_or_update" resolves by
            // recordId presence. Anything else (including plain "create") is create. Shared by
            // A3's prefill trigger and A4's submit branching so the two can never disagree about
            // which mode is in effect.
            resolveEffectiveMode: function() {
                if (this.runtimeInviteContext && this.runtimeInviteContext.token) return "update";
                var declared = this.readFormOption(["prm:on-submit", "prm-on-submit", "on-submit", "onSubmit"], "create");
                if (declared === "update") return "update";
                if (declared === "create_or_update") {
                    return this.resolveRecordContext().recordId ? "update" : "create";
                }
                return "create";
            },
            // A3: on mount, when the effective mode is "update" and a recordId is available,
            // loads the record and populates ONLY the mapped fields (the inverse of
            // store.__getMappedValues()) -- loading/sending the full record would blank out
            // whatever this particular form doesn't expose as a field.
            loadRecordForPrefill: async function() {
                var store = this.formStore;
                if (this.resolveEffectiveMode() !== "update") return;

                var inviteContext = this.runtimeInviteContext;
                if (inviteContext && inviteContext.token) {
                    var inviteRecord = inviteContext.record || {};
                    var inviteMapping = store.getMapping();
                    var inviteValues = {};
                    Object.keys(inviteMapping).forEach(function(fieldName) {
                        var columnName = inviteMapping[fieldName];
                        if (Object.prototype.hasOwnProperty.call(inviteRecord, columnName)) inviteValues[fieldName] = inviteRecord[columnName];
                    });
                    store.setValues(inviteValues);
                    return;
                }

                var recordContext = this.resolveRecordContext();
                // No recordId here is not this method's error to raise -- a pure update form
                // with no id fails loudly on submit (A1); a dual form with no id is legitimately
                // in create mode and has nothing to prefill.
                if (!recordContext.recordId) return;

                var targetTable = recordContext.table || this.readFormOption(["prm:target-table", "prm-target-table", "target-table", "targetTable"], "");
                if (!targetTable) return;

                var gate = this.resolveGate();
                if (!gate || !gate.api || !gate.api.record || !gate.api.record.get) return;

                store.__state.loading = true;
                try {
                    var response = await gate.api.record.get({ table: targetTable, id: recordContext.recordId });
                    var record = this.resolveCreatedRecord(response);
                    if (!record) return;

                    var mapping = store.getMapping();
                    var formValues = {};
                    Object.keys(mapping).forEach(function(fieldName) {
                        var columnName = mapping[fieldName];
                        if (Object.prototype.hasOwnProperty.call(record, columnName)) {
                            formValues[fieldName] = record[columnName];
                        }
                    });
                    store.setValues(formValues);
                } catch (error) {
                    console.error("[prm-form] Failed to load record for prefill", error);
                    this.fireEvent("onError", [store, { error: error }]);
                    if (gate && gate.ui && gate.ui.notify) {
                        await gate.ui.notify(
                            error && error.message ? error.message : runtimeText("forms.error.load_record", "Failed to load record"),
                            "error"
                        );
                    }
                } finally {
                    store.__state.loading = false;
                }
            },
            resolveSessionUserId: function(session) {
                if (!session) return "";
                if (session.user && session.user.id) return String(session.user.id);
                if (session.data && session.data.user && session.data.user.id) return String(session.data.user.id);
                if (session.data && session.data.id) return String(session.data.id);
                if (session.id) return String(session.id);
                return "";
            },
            parseRequestStatusMapping: function(value) {
                if (!value) return {};
                if (typeof value === "object") return value;
                try { return JSON.parse(String(value)); } catch (_error) { return {}; }
            },
            normalizeRequestStatusValue: function(value) {
                if (value === null || value === undefined) return "";
                if (typeof value === "object") return String(value.value || value.id || value.display || "");
                return String(value);
            },
            displayRequestStatusValue: function(value) {
                if (value === null || value === undefined) return "";
                if (typeof value === "object") return String(value.display || value.label || value.value || value.id || JSON.stringify(value));
                return String(value);
            },
            normalizeGateRecordList: function(result) {
                if (Array.isArray(result)) return result;
                if (result && Array.isArray(result.data)) return result.data;
                if (result && Array.isArray(result.records)) return result.records;
                return [];
            },
            firstGateRecord: async function(gate, table, filters) {
                if (!gate || !gate.api || !gate.api.record || !gate.api.record.query) return null;
                var result = await gate.api.record.query({ table: table, filters: filters, limit: 1, referenceMode: "display" });
                return this.normalizeGateRecordList(result)[0] || null;
            },
            resolveRequestStatusColumn: async function(gate, targetTable, statusField) {
                if (!targetTable || !statusField) return null;
                var sysTable = await this.firstGateRecord(gate, "sys_tables", [{ field: "name", operator: "eq", value: targetTable }]);
                if (!sysTable || !sysTable.id) return null;
                var column = await this.firstGateRecord(gate, "sys_columns", [
                    { field: "sys_table_id", operator: "eq", value: sysTable.id },
                    { field: "name", operator: "eq", value: statusField }
                ]);
                if (column) column.__tableId = sysTable.id;
                return column;
            },
            resolveRequestStatusChoiceDisplay: async function(gate, statusColumn, value) {
                var normalized = String(value || "").trim();
                if (!statusColumn || String(statusColumn.column_type || "").toUpperCase() !== "CHOICE" || !normalized) return "";
                var choice = await this.firstGateRecord(gate, "sys_choice", [
                    { field: "ref_table", operator: "eq", value: statusColumn.__tableId },
                    { field: "ref_column", operator: "eq", value: statusColumn.id },
                    { field: "value", operator: "eq", value: normalized }
                ]);
                return choice ? String(choice.label || choice.display || choice.title || choice.name || choice.value || normalized) : "";
            },
            mapRequestStatusState: function(value, mapping) {
                var normalized = String(value || "").trim();
                var states = ["open", "in_progress", "closed", "cancelled"];
                for (var i = 0; i < states.length; i++) {
                    var state = states[i];
                    var values = Array.isArray(mapping && mapping[state]) ? mapping[state] : [];
                    for (var j = 0; j < values.length; j++) {
                        if (String(values[j]).trim() === normalized) return state;
                    }
                }
                return "";
            },
            resolveRequestStatusInfo: async function(catalog, targetTable, targetRecord) {
                var gate = this.resolveGate();
                var statusField = String(catalog && catalog.request_status_field || "").trim();
                if (!statusField || !targetRecord || !Object.prototype.hasOwnProperty.call(targetRecord, statusField)) {
                    return { configured: false, state: "open", value: "", display: "", syncedAt: null };
                }
                var rawValue = targetRecord[statusField];
                var value = this.normalizeRequestStatusValue(rawValue);
                var mappedState = this.mapRequestStatusState(value, this.parseRequestStatusMapping(catalog.request_status_mapping_json));
                var display = this.displayRequestStatusValue(rawValue);
                try {
                    var statusColumn = await this.resolveRequestStatusColumn(gate, targetTable, statusField);
                    display = await this.resolveRequestStatusChoiceDisplay(gate, statusColumn, value) || display;
                } catch (_displayError) {
                    display = display || value;
                }
                return {
                    configured: true,
                    state: mappedState || "open",
                    value: value,
                    display: display || value,
                    syncedAt: new Date().toISOString()
                };
            },
            snapshotFieldValue: function(value) {
                if (value === null || value === undefined) return null;
                if (value instanceof Date) return value.toISOString();
                if (typeof File !== "undefined" && value instanceof File) return { name: value.name, size: value.size, type: value.type };
                if (Array.isArray(value)) return value.map((item) => this.snapshotFieldValue(item));
                if (typeof value === "object") {
                    if (value.id || value.display || value.title || value.name || value.number_ref) {
                        return {
                            id: value.id || null,
                            display: value.display || value.title || value.name || value.number_ref || value.id || ""
                        };
                    }
                    try { return JSON.parse(JSON.stringify(value)); } catch (_err) { return String(value); }
                }
                return value;
            },
            snapshotDisplayValue: function(value) {
                if (value === null || value === undefined || value === "") return "";
                if (typeof value === "boolean") return value ? runtimeText("forms.boolean.yes", "Yes") : runtimeText("forms.boolean.no", "No");
                if (Array.isArray(value)) return value.map((item) => this.snapshotDisplayValue(item)).filter(Boolean).join(", ");
                if (typeof File !== "undefined" && value instanceof File) return value.name;
                if (typeof value === "object") return value.display || value.title || value.name || value.number_ref || value.id || JSON.stringify(value);
                return String(value);
            },
            buildServicePortalSnapshot: function() {
                var store = this.formStore;
                if (!store || !store.__fieldOrder || !store.__fieldDefs || !store.__getFieldState) return [];
                var snapshot = [];
                store.__fieldOrder.forEach((name) => {
                    var def = store.__fieldDefs[name] || {};
                    var state = store.__getFieldState(name);
                    if (!state || !state.visible) return;
                    var value = store.getValue(name);
                    if (value === null || value === undefined || value === "") return;
                    snapshot.push({
                        name: name,
                        label: resolveLabel(state.label || def.label || name),
                        type: def.type || "",
                        value: this.snapshotFieldValue(value),
                        display_value: this.snapshotDisplayValue(value)
                    });
                });
                return snapshot;
            },
            createServicePortalRequest: async function(options) {
                var gate = this.resolveGate();
                var catalog = this.resolveServicePortalCatalog();
                if (!gate || !gate.api || !gate.api.record || !gate.api.record.create) return "";
                if (!catalog || !catalog.id || !options || !options.targetTable || !options.targetRecordId) return "";

                var session = null;
                if (gate.api.auth && gate.api.auth.getSession) {
                    try { session = await gate.api.auth.getSession(); } catch (_sessionError) { session = null; }
                }

                var targetRecord = options.targetRecord || {};
                var statusInfo = await this.resolveRequestStatusInfo(catalog, options.targetTable, targetRecord);
                var title = catalog.title || catalog.name || targetRecord.title || targetRecord.name || targetRecord.number_ref || "";
                var payload = {
                    form_catalog_id: catalog.id,
                    requester_id: this.resolveSessionUserId(session) || null,
                    table_name: options.targetTable,
                    target_record_id: options.targetRecordId,
                    state: statusInfo.state || "open",
                    submitted_at: new Date().toISOString(),
                    title: title,
                    target_status_value: statusInfo.value || "",
                    target_status_display: statusInfo.display || "",
                    status_synced_at: statusInfo.syncedAt,
                    last_activity_at: targetRecord.updated_at || targetRecord.created_at || new Date().toISOString(),
                    submitted_values_snapshot_json: JSON.stringify(this.buildServicePortalSnapshot())
                };

                var response = await gate.api.record.create({ table: "sys_sp_requests", data: payload });
                return this.resolveCreatedRecordId(response);
            },
            createDraftToken: function() {
                if (window.crypto && window.crypto.randomUUID) return window.crypto.randomUUID().replace(/-/g, "");
                return "draft_" + Date.now() + "_" + Math.random().toString(36).slice(2);
            },
            attachmentErrorMessage: function(error) {
                var code = error && (error.code || (error.response && error.response.data && error.response.data.error && error.response.data.error.code));
                var message = String(error && error.message || "");
                if (code === "ATTACHMENT_FIELD_NOT_ALLOWED") return runtimeText("forms.attachment.error.field_not_allowed", "This attachment field is unavailable.");
                if (code === "ATTACHMENT_VALIDATION_ERROR" && /larger than|size|limit/i.test(message)) return runtimeText("forms.attachment.error.size", "The file exceeds the allowed size.");
                if (code === "ATTACHMENT_VALIDATION_ERROR" && /type|extension/i.test(message)) return runtimeText("forms.attachment.error.type", "This file type is not allowed.");
                return runtimeText("forms.attachment.error.generic", "Could not upload the attachment. Please try again.");
            },
            uploadAttachmentFields: async function(options) {
                var store = this.formStore;
                var gate = this.resolveGate();
                var servicePortalFormId = options && options.servicePortalFormId ? options.servicePortalFormId : "";
                var canUsePortalAttachments = servicePortalFormId && gate && gate.api && gate.api.servicePortal && gate.api.servicePortal.uploadAttachment;
                var canUseGenericAttachments = gate && gate.api && gate.api.attachments && gate.api.attachments.upload;

                if (!canUsePortalAttachments && !canUseGenericAttachments) return null;

                var attachmentFields = store.__fieldOrder.filter(function(name) {
                    var def = store.__fieldDefs[name];
                    return def && def.type === "attachment" && store.__getFieldState(name).visible;
                });
                if (!attachmentFields.length) return null;

                var draftToken = options.draftToken || (options.recordId ? null : this.createDraftToken());
                var uploadedAny = false;
                for (var i = 0; i < attachmentFields.length; i++) {
                    var fieldName = attachmentFields[i];
                    var def = store.__fieldDefs[fieldName];
                    var value = store.getValue(fieldName);
                    var files = Array.isArray(value) ? value : (value ? [value] : []);
                    for (var f = 0; f < files.length; f++) {
                        var file = files[f];
                        if (!(typeof File !== "undefined" && file instanceof File)) continue;
                        try {
                        if (canUsePortalAttachments) {
                            await gate.api.servicePortal.uploadAttachment({
                                formId: servicePortalFormId,
                                draftToken: draftToken || undefined,
                                column: def.map || def.name,
                                file: file
                            });
                        } else {
                            await gate.api.attachments.upload({
                                table: options.table,
                                recordId: options.recordId || undefined,
                                draftToken: draftToken || undefined,
                                column: def.map || def.name,
                                file: file
                            });
                        }
                        uploadedAny = true;
                        } catch (error) {
                            var uploadError = new Error(this.attachmentErrorMessage(error));
                            uploadError.code = error && error.code;
                            throw uploadError;
                        }
                    }
                }

                return uploadedAny ? draftToken : null;
            },
            handleSubmit: async function() {
                var store = this.formStore;
                if (!store || store.__state.submitting) return;
                store.__state.effectiveMode = this.resolveEffectiveMode();

                // Validate all fields
                if (!store.validate()) {
                    if (Vue.nextTick) await Vue.nextTick();
                    var fieldOrder = store.__fieldOrder || Object.keys(store.__state.fieldErrors || {});
                    var controls = this.$el ? this.$el.querySelectorAll("[id]") : [];
                    for (var i = 0; i < fieldOrder.length; i++) {
                        var fieldName = fieldOrder[i];
                        if (!store.__state.fieldErrors[fieldName] || !store.__state.fieldErrors[fieldName].length) continue;
                        var control = Array.prototype.find.call(controls, function(node) { return node.id === fieldName; });
                        if (control && typeof control.focus === "function") {
                            control.focus();
                            break;
                        }
                    }
                    return;
                }

                // Fire onSubmit event (can cancel). event.record is the final payload object.
                var mappedValues = store.__getMappedValues();
                var mapping = store.getMapping();
                var submitEvent = {
                    values: store.getValues(),
                    mapping: mapping,
                    record: mappedValues
                };
                var result = await this.fireEvent("onSubmit", [store, submitEvent]);
                if (result === false) return;
                mappedValues = submitEvent.record || mappedValues;

                var targetTable = this.readFormOption(["prm:target-table", "prm-target-table", "target-table", "targetTable"], "");
                var action = this.readFormOption(["prm:on-submit", "prm-on-submit", "on-submit", "onSubmit"], "create");
                var successMsg = this.readFormOption(["prm:success-message", "prm-success-message", "success-message", "successMessage"], "");
                var successRedirect = this.readFormOption(["prm:success-redirect", "prm-success-redirect", "success-redirect", "successRedirect"], "");
                // A2: recordId now resolved the same way for submit and prefill (A3) -- host
                // context first, static prm:record-id attribute as fallback.
                var recordId = this.resolveRecordContext().recordId;
                // A4: the mode this specific submission runs as. For "update" (pure) this is
                // always "update" -- effective and declared agree. For "create_or_update" it
                // depends on recordId. See resolveEffectiveMode's own comment for why the A1
                // guard right below stays on the DECLARED action, not this.
                var effectiveMode = this.resolveEffectiveMode();

                if (!targetTable) {
                    console.error("[prm-form] No target table specified (prm:target-table)");
                    return;
                }

                // A1 (design_form_update_mode_and_fluss_dispatch.md): a pure "update" form with
                // no recordId used to fall through to the "create" branch below in silence,
                // producing a spurious record instead of updating anything -- there is today no
                // way to even supply a recordId, so this was the NORMAL case, not an edge case.
                // Fail loud instead: never create on behalf of an update form.
                if (action === "update" && !recordId && !(this.runtimeInviteContext && this.runtimeInviteContext.token)) {
                    var missingRecordIdError = new Error(runtimeText("forms.error.missing_record_id", "This form updates a record, but no record id was provided (prm:record-id)."));
                    this.fireEvent("onError", [store, { error: missingRecordIdError }]);
                    var gateForMissingRecordId = this.resolveGate();
                    if (gateForMissingRecordId && gateForMissingRecordId.ui && gateForMissingRecordId.ui.notify) {
                        await gateForMissingRecordId.ui.notify(missingRecordIdError.message, "error");
                    }
                    return;
                }

                store.__state.submitting = true;

                try {
                    var response;
                    var gate = this.resolveGate();
                    var servicePortalCatalog = this.resolveServicePortalCatalog();
                    var inviteContext = this.runtimeInviteContext;
                    // A4: looks at the EFFECTIVE mode, not the declared one -- in "create_or_update"
                    // without a recordId, this submission IS a create and must get the same
                    // delegated Service Portal path (and sys_sp_requests wrapper below) any other
                    // create gets. Getting this wrong is exactly the dual mode regression the
                    // design doc calls out.
                    var useDelegatedServicePortalSubmit = effectiveMode !== "update" && servicePortalCatalog && servicePortalCatalog.id && gate && gate.api && gate.api.servicePortal && gate.api.servicePortal.submit;

                    if (inviteContext && inviteContext.token) {
                        var inviteDraftToken = await this.uploadAttachmentFields({
                            table: targetTable, servicePortalFormId: servicePortalCatalog.id, draftToken: inviteContext.token
                        });
                        response = await gate.api.servicePortal.submitInvite({
                            token: inviteContext.token, record: mappedValues, attachmentDraftToken: inviteDraftToken || undefined
                        });
                    } else if (effectiveMode === "update") {
                        response = await gate.api.record.update({
                            table: targetTable,
                            id: recordId,
                            data: mappedValues
                        });
                        await this.uploadAttachmentFields({ table: targetTable, recordId: recordId });
                    } else if (useDelegatedServicePortalSubmit) {
                        var delegatedDraftToken = await this.uploadAttachmentFields({
                            table: targetTable,
                            servicePortalFormId: servicePortalCatalog.id
                        });
                        response = await gate.api.servicePortal.submit({
                            formId: servicePortalCatalog.id,
                            targetTable: targetTable,
                            record: mappedValues,
                            attachmentDraftToken: delegatedDraftToken || undefined,
                            requestSnapshot: this.buildServicePortalSnapshot()
                        });
                    } else {
                        var draftToken = await this.uploadAttachmentFields({ table: targetTable });
                        if (draftToken) mappedValues._attachment_draft_token = draftToken;
                        response = await gate.api.record.create({
                            table: targetTable,
                            data: mappedValues
                        });
                    }

                    store.__state.submitted = true;

                    this.fireEvent("onSuccess", [store, { record: response, response: response }]);

                    if (successMsg) {
                        var msg = resolveLabel(successMsg);
                        await gate.ui.notify(msg, "success");
                    } else if (effectiveMode === "update" && gate && gate.ui && gate.ui.notify) {
                        await gate.ui.notify(runtimeText("forms.submit.update_success", "Record updated successfully"), "success");
                    }

                    if (successRedirect) {
                        await gate.router.navigate(successRedirect);
                    } else if (effectiveMode !== "update") {
                        var createdRecordId = this.resolveCreatedRecordId(response) || (response && (response.target_record_id || response.targetRecordId));
                        if (createdRecordId) {
                            var requestRecordId = response && (response.request_id || response.requestId) || "";
                            if (!useDelegatedServicePortalSubmit) {
                                try {
                                    requestRecordId = await this.createServicePortalRequest({
                                        targetTable: targetTable,
                                        targetRecordId: createdRecordId,
                                        targetRecord: this.resolveCreatedRecord(response)
                                    });
                                } catch (requestError) {
                                    console.error("[prm-form] Failed to create sys_sp_requests wrapper", requestError);
                                }
                            }
                            var detailBasePath = gate.router && gate.router.path && String(gate.router.path).indexOf("/pub/") === 0 ? "/pub/request-detail" : "/g/request-detail";
                            var submittedSuffix = detailBasePath.indexOf("/pub/") === 0 ? "&submitted=1" : "";
                            await gate.router.navigate(detailBasePath + "?requestId=" + encodeURIComponent(requestRecordId || createdRecordId) + submittedSuffix);
                        }
                    }

                } catch (error) {
                    this.fireEvent("onError", [store, { error: error }]);
                    var gate = this.resolveGate();
                    if (gate && gate.ui && gate.ui.notify) {
                        await gate.ui.notify(
                            error && error.message ? error.message : runtimeText("forms.error.submit_failed", "Form submission failed"),
                            "error"
                        );
                    }
                } finally {
                    store.__state.submitting = false;
                }
            }
        },
        template: '<form class="prm-form" @submit.prevent="handleSubmit" novalidate>' +
            '<div v-if="formStore && formStore.__isMultiStep" class="prm-stepper">' +
                '<div v-for="(step, idx) in formStore.__steps" :key="idx" ' +
                'class="prm-stepper__step" :class="stepperStepClasses(idx)">' +
                    '<span class="prm-stepper__number">{{ idx + 1 }}</span>' +
                    '<span class="prm-stepper__title">' + '{{ stepTitle(step, idx) }}' + '</span>' +
                '</div>' +
            '</div>' +
            '<slot />' +
        '</form>'
    };

    Object.keys(window.__prmComponents).forEach(function(name) {
        var component = window.__prmComponents[name];
        if (!component || typeof component !== "object") return;
        component.__prmTagName = name;
        if (!component.name) component.name = name;
        if (!component.displayName) component.displayName = name;
    });

})();
`;
};
